import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Grid,
  Avatar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WebIcon from "@mui/icons-material/Web";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BusinessIcon from "@mui/icons-material/Business";
import TargetIcon from "@mui/icons-material/GpsFixed";
import StarIcon from "@mui/icons-material/Star";
import HelpOutline from "@mui/icons-material/HelpOutline";
import WarningIcon from "@mui/icons-material/Warning";
import CodeIcon from "@mui/icons-material/Code";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DocumentPreview from "./DocumentPreview";
import SkillHighlighting from "./SkillHighlighting";
import '../styles/layout.css';

const toArray = (val) => Array.isArray(val) ? val : (val == null ? [] : [val]);
const formatScore = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "--";
  const n = Number(v);
  return Math.round(n);
};

// Simplified color palette - only 3 main colors
const colors = {
  primary: '#1976d2',      // Blue
  success: '#2e7d32',      // Green  
  error: '#d32f2f',        // Red
  neutral: '#6c757d',      // Gray
  light: '#f8f9fa',        // Light gray
  dark: '#2c3e50'          // Dark gray
};

// Unified base font for this component (force on all descendants)
const baseFont = `'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif`;


// Use raw database scores without corrections - just highlight certifications
const validateScoring = (result) => {
  if (!result || !result.keywords || !result.scores) return result;
  
  // Just return the result as-is from the database
  // No scoring corrections, no bonus points
  return result;
};


// Format education value which might be a string, object, or array of those
const formatEducation = (education) => {
  if (education == null) return '';
  if (typeof education === 'string' || typeof education === 'number') return String(education);
  if (Array.isArray(education)) {
    return education
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'string' || typeof item === 'number') return String(item);
        if (typeof item === 'object') {
          const degree = item.degree || item.title || '';
          const institution = item.institution || item.school || '';
          const grad = item.graduationDate || item.graduated || item.year || '';
          const parts = [degree, institution].filter(Boolean).join(' - ');
          return grad ? `${parts}${parts ? ' ' : ''}(${grad})` : parts || 'Education details not available';
        }
        return String(item);
      })
      .filter(Boolean)
      .join(', ');
  }
  if (typeof education === 'object') {
    const degree = education.degree || education.title || '';
    const institution = education.institution || education.school || '';
    const grad = education.graduationDate || education.graduated || education.year || '';
    const parts = [degree, institution].filter(Boolean).join(' - ');
    return grad ? `${parts}${parts ? ' ' : ''}(${grad})` : parts || 'Education details not available';
  }
  return String(education);
};

// Smart location extraction with priority system
const getSmartLocation = (candidateMetadata, experienceTimeline, educationDetails) => {
  // Priority 1: Personal details location
  const personalLocation = candidateMetadata?.location;
  if (personalLocation && personalLocation.trim() !== '') {
    return {
      location: personalLocation,
      source: 'personal',
      displayText: personalLocation
    };
  }

  // Priority 2: Recent job location (most recent experience)
  const recentJobLocation = experienceTimeline && experienceTimeline.length > 0 
    ? experienceTimeline[0]?.location 
    : null;
  if (recentJobLocation && recentJobLocation.trim() !== '') {
    return {
      location: recentJobLocation,
      source: 'recent_job',
      displayText: `${recentJobLocation} (from latest job)`
    };
  }

  // Priority 3: University location (extract from education details)
  const universityLocation = extractUniversityLocation(educationDetails);
  if (universityLocation) {
    return {
      location: universityLocation,
      source: 'university',
      displayText: `${universityLocation} (from university)`
    };
  }

  // Priority 4: Default no location
  return {
    location: null,
    source: 'none',
    displayText: 'Location not found'
  };
};

// Extract university information from education details
const extractUniversityInfo = (educationDetails) => {
  if (!educationDetails) return { university: null, location: null };
  
  // Check if educationDetails is a string and contains university info
  if (typeof educationDetails === 'string') {
    // Look for university patterns - more comprehensive patterns
    const universityPatterns = [
      // Pattern for "University, City, State/Country" format
      /([A-Z][A-Za-z\s\.]+(?:University|College|Institute|School))\s*,\s*([A-Z][A-Za-z\s]+?)(?:\s*,\s*([A-Z][A-Za-z\s]+))?$/i,
      // Pattern for "at University, City, State/Country" format
      /(?:at|from|in)\s+([A-Z][A-Za-z\s\.]+(?:University|College|Institute|School))\s*,\s*([A-Z][A-Za-z\s]+?)(?:\s*,\s*([A-Z][A-Za-z\s]+))?$/i,
      // Pattern for "University of X" format
      /([A-Z][A-Za-z\s\.]+(?:University|College|Institute|School))\s+of\s+([A-Z][A-Za-z\s]+?)(?:\s*,\s*([A-Z][A-Za-z\s]+))?$/i,
      // Pattern for "X University" format
      /([A-Z][A-Za-z\s\.]+)\s+(?:University|College|Institute|School)(?:\s*,\s*([A-Z][A-Za-z\s]+?)(?:\s*,\s*([A-Z][A-Za-z\s]+))?)?$/i,
      // Pattern for "B.S. in Computer Science from University of California, Berkeley" format
      /(?:from|at)\s+([A-Z][A-Za-z\s\.]+(?:University|College|Institute|School))\s+(?:of\s+)?([A-Z][A-Za-z\s]+?)(?:\s*,\s*([A-Z][A-Za-z\s]+))?/i,
      // Pattern for "University of California, Berkeley" format (without "from")
      /([A-Z][A-Za-z\s\.]+(?:University|College|Institute|School))\s+(?:of\s+)?([A-Z][A-Za-z\s]+?)(?:\s*,\s*([A-Z][A-Za-z\s]+))?/i,
      // Pattern for "Berkeley" or "Stanford" (just university names)
      /\b(Stanford|Harvard|MIT|Berkeley|UCLA|UCSD|UCSF|Caltech|Carnegie Mellon|CMU|Georgia Tech|Georgia Institute of Technology|University of California|UC|University of Texas|UT|University of Michigan|UMich|University of Illinois|UIUC|University of Washington|UW|University of Wisconsin|UW-Madison|University of North Carolina|UNC|Duke|Northwestern|University of Chicago|UChicago|University of Pennsylvania|UPenn|Princeton|Yale|Columbia|Cornell|Brown|Dartmouth|University of Virginia|UVA|University of North Carolina|UNC|Duke|Northwestern|University of Chicago|UChicago|University of Pennsylvania|UPenn|Princeton|Yale|Columbia|Cornell|Brown|Dartmouth|University of Virginia|UVA)\b/i
    ];
    
    for (let i = 0; i < universityPatterns.length; i++) {
      const pattern = universityPatterns[i];
      const match = educationDetails.match(pattern);
      
      if (match) {
        let university, city, stateCountry;
        
        if (i === universityPatterns.length - 1) {
          // Last pattern - just university name
          university = match[1]?.trim();
          city = null;
          stateCountry = null;
        } else {
          university = match[1]?.trim();
          city = match[2]?.trim();
          stateCountry = match[3]?.trim();
        }
        
        let location = null;
        if (city && stateCountry) {
          location = `${city}, ${stateCountry}`;
        } else if (city) {
          location = city;
        }
        
        return { university, location };
      }
    }
  }
  
  // Check if educationDetails is an object with university info
  if (typeof educationDetails === 'object') {
    return {
      university: educationDetails.university || educationDetails.institution || educationDetails.school || null,
      location: educationDetails.location || educationDetails.city || educationDetails.state || null
    };
  }
  
  return { university: null, location: null };
};

// Extract university location from education details (legacy function for backward compatibility)
const extractUniversityLocation = (educationDetails) => {
  const { location } = extractUniversityInfo(educationDetails);
  return location;
};

// Extract most recent job location
const extractRecentJobLocation = (experienceTimeline) => {
  if (!experienceTimeline || experienceTimeline.length === 0) return null;
  
  const mostRecentJob = experienceTimeline[0];
  return mostRecentJob?.location || mostRecentJob?.city || mostRecentJob?.state || null;
};

// Normalize location for comparison
const normalizeLocation = (location) => {
  if (!location) return null;
  
  const normalized = location.toLowerCase().trim();
  
  // Remove common suffixes that don't affect location meaning
  const cleaned = normalized
    .replace(/\s+(remote|work from home|wfh)$/i, '') // Remove remote work indicators
    .replace(/\s+(usa|united states|us)$/i, '') // Remove country suffixes
    .replace(/\s*,\s*$/, ''); // Remove trailing commas
  
  return cleaned;
};

// State code to full name mapping
const stateCodeToName = {
  'ca': 'california',
  'tx': 'texas',
  'fl': 'florida',
  'ny': 'new york',
  'il': 'illinois',
  'pa': 'pennsylvania',
  'oh': 'ohio',
  'ga': 'georgia',
  'nc': 'north carolina',
  'mi': 'michigan',
  'nj': 'new jersey',
  'va': 'virginia',
  'wa': 'washington',
  'az': 'arizona',
  'ma': 'massachusetts',
  'tn': 'tennessee',
  'in': 'indiana',
  'mo': 'missouri',
  'md': 'maryland',
  'wi': 'wisconsin',
  'co': 'colorado',
  'mn': 'minnesota',
  'sc': 'south carolina',
  'al': 'alabama',
  'la': 'louisiana',
  'ky': 'kentucky',
  'or': 'oregon',
  'ok': 'oklahoma',
  'ct': 'connecticut',
  'ut': 'utah',
  'ia': 'iowa',
  'nv': 'nevada',
  'ar': 'arkansas',
  'ms': 'mississippi',
  'ks': 'kansas',
  'nm': 'new mexico',
  'ne': 'nebraska',
  'wv': 'west virginia',
  'id': 'idaho',
  'hi': 'hawaii',
  'nh': 'new hampshire',
  'me': 'maine',
  'mt': 'montana',
  'ri': 'rhode island',
  'de': 'delaware',
  'sd': 'south dakota',
  'nd': 'north dakota',
  'ak': 'alaska',
  'vt': 'vermont',
  'wy': 'wyoming'
};

// Helper function to capitalize state names properly
const capitalizeStateName = (stateName) => {
  if (!stateName) return stateName;
  
  // Handle special cases
  const specialCases = {
    'new york': 'New York',
    'new jersey': 'New Jersey',
    'new mexico': 'New Mexico',
    'new hampshire': 'New Hampshire',
    'north carolina': 'North Carolina',
    'north dakota': 'North Dakota',
    'south carolina': 'South Carolina',
    'south dakota': 'South Dakota',
    'west virginia': 'West Virginia',
    'rhode island': 'Rhode Island',
    'united states': 'United States',
    'united kingdom': 'United Kingdom',
    'south korea': 'South Korea',
    'south africa': 'South Africa',
    'new zealand': 'New Zealand',
    'sri lanka': 'Sri Lanka',
    'costa rica': 'Costa Rica',
    'el salvador': 'El Salvador',
    'saudi arabia': 'Saudi Arabia',
    'united arab emirates': 'United Arab Emirates',
    'trinidad and tobago': 'Trinidad and Tobago',
    'papua new guinea': 'Papua New Guinea',
    'bosnia and herzegovina': 'Bosnia and Herzegovina',
    'czech republic': 'Czech Republic',
    'dominican republic': 'Dominican Republic',
    'central african republic': 'Central African Republic',
    'democratic republic of the congo': 'Democratic Republic of the Congo',
    'republic of the congo': 'Republic of the Congo',
    'ivory coast': 'Ivory Coast',
    'burkina faso': 'Burkina Faso',
    'sierra leone': 'Sierra Leone',
    'guinea bissau': 'Guinea-Bissau',
    'cape verde': 'Cape Verde',
    'sao tome and principe': 'São Tomé and Príncipe',
    'equatorial guinea': 'Equatorial Guinea',
    'central african republic': 'Central African Republic',
    'chad': 'Chad',
    'cameroon': 'Cameroon',
    'gabon': 'Gabon',
    'congo': 'Congo',
    'angola': 'Angola',
    'zambia': 'Zambia',
    'malawi': 'Malawi',
    'mozambique': 'Mozambique',
    'madagascar': 'Madagascar',
    'mauritius': 'Mauritius',
    'seychelles': 'Seychelles',
    'comoros': 'Comoros',
    'mayotte': 'Mayotte',
    'reunion': 'Réunion',
    'saint helena': 'Saint Helena',
    'ascension': 'Ascension',
    'tristan da cunha': 'Tristan da Cunha'
  };
  
  const lowerState = stateName.toLowerCase();
  if (specialCases[lowerState]) {
    return specialCases[lowerState];
  }
  
  // Default capitalization for single words
  return stateName.charAt(0).toUpperCase() + stateName.slice(1).toLowerCase();
};

// Extract and normalize state/region from location
const extractStateRegion = (location) => {
  if (!location) return null;
  
  const normalized = location.toLowerCase().trim();
  
  // First check for two-letter state codes
  const stateCodeMatch = normalized.match(/\b([a-z]{2})\b$/);
  if (stateCodeMatch) {
    const code = stateCodeMatch[1];
    // Convert state code to full name for consistent comparison
    return stateCodeToName[code] || code;
  }
  
  // Then check for full state names
  const statePatterns = [
    /\b(california|texas|florida|new york|illinois|pennsylvania|ohio|georgia|north carolina|michigan|new jersey|virginia|washington|arizona|massachusetts|tennessee|indiana|missouri|maryland|wisconsin|colorado|minnesota|south carolina|alabama|louisiana|kentucky|oregon|oklahoma|connecticut|utah|iowa|nevada|arkansas|mississippi|kansas|new mexico|nebraska|west virginia|idaho|hawaii|new hampshire|maine|montana|rhode island|delaware|south dakota|north dakota|alaska|vermont|wyoming)\b/,
    // Country names
    /\b(usa|united states|canada|mexico|uk|united kingdom|germany|france|india|china|japan|australia|brazil|italy|spain|netherlands|sweden|norway|denmark|finland|singapore|south korea|israel|ireland|switzerland|austria|belgium|poland|portugal|greece|turkey|russia|ukraine|south africa|egypt|nigeria|kenya|morocco|tunisia|algeria|ghana|ethiopia|uganda|tanzania|zimbabwe|botswana|namibia|zambia|malawi|mozambique|madagascar|mauritius|seychelles|comoros|mayotte|reunion|saint helena|ascension|tristan da cunha)\b/
  ];
  
  for (const pattern of statePatterns) {
    const match = normalized.match(pattern);
    if (match) {
      return match[1] || match[0];
    }
  }
  
  return null;
};

// Check if two locations are in the same region/state
const areLocationsInSameRegion = (loc1, loc2) => {
  if (!loc1 || !loc2) return false;
  
  const state1 = extractStateRegion(loc1);
  const state2 = extractStateRegion(loc2);
  
  // If both have states and they match, they're in the same region
  if (state1 && state2) {
    return state1 === state2;
  }
  
  // If one has a state and the other contains that state, they're in the same region
  if (state1 && loc2.toLowerCase().includes(state1)) return true;
  if (state2 && loc1.toLowerCase().includes(state2)) return true;
  
  // If both are just city names without states, consider them different
  return false;
};

// Check for location inconsistencies
const checkLocationInconsistencies = (candidateMetadata, experienceTimeline, educationDetails) => {
  const locations = [];
  
  // Personal location
  if (candidateMetadata?.location) {
    locations.push({ location: candidateMetadata.location, source: 'personal' });
  }
  
  // Recent job location
  const recentJobLocation = extractRecentJobLocation(experienceTimeline);
  if (recentJobLocation) {
    locations.push({ location: recentJobLocation, source: 'recent_job' });
  }
  
  // University location
  const { location: universityLocation } = extractUniversityInfo(educationDetails);
  if (universityLocation) {
    locations.push({ location: universityLocation, source: 'university' });
  }
  
  // Normalize locations for comparison
  const normalizedLocations = locations.map(loc => ({
    ...loc,
    normalized: normalizeLocation(loc.location)
  }));
  
  // Check for actual inconsistencies (not just different cities in same state)
  let hasInconsistencies = false;
  const inconsistentGroups = [];
  
  for (let i = 0; i < normalizedLocations.length; i++) {
    for (let j = i + 1; j < normalizedLocations.length; j++) {
      const loc1 = normalizedLocations[i];
      const loc2 = normalizedLocations[j];
      
      // Skip if locations are identical
      if (loc1.normalized === loc2.normalized) continue;
      
      // Check if they're in the same region/state
      const sameRegion = areLocationsInSameRegion(loc1.location, loc2.location);
      
      if (!sameRegion) {
        hasInconsistencies = true;
        // Group inconsistent locations
        const group = [loc1, loc2];
        inconsistentGroups.push(group);
      }
    }
  }
  
  return {
    locations,
    hasInconsistencies,
    inconsistentGroups,
    normalizedLocations
  };
};

// Test function for location logic (can be removed in production)
const testLocationLogic = () => {};


// New ScoreCircle component for tabbed interface
const ScoreCircle = ({ score, max, label, color }) => {
  const percentage = (score / max) * 100;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative', width: 112, height: 112 }}>
      <CircularProgress
        variant="determinate"
          value={percentage}
          size={112}
          thickness={4}
          sx={{ 
            color: color,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }}
      />
      <Box
        sx={{
          position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          display: 'flex',
            flexDirection: 'column',
          alignItems: 'center',
        }}
      >
          <Typography variant="h4" fontWeight={700} color="text.primary">
            {score}
        </Typography>
      </Box>
      </Box>
      <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ mt: 1, fontSize: '1rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.primary" sx={{ opacity: 0.8, fontSize: '0.9rem' }}>
        {score}/{max}
      </Typography>
    </Box>
  );
};

// StatCard component for quick stats
const StatCard = ({ icon: Icon, label, value, sublabel, color = 'primary' }) => (
  <Card sx={{ 
    p: 3, 
    height: '100%',
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.06)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      transform: 'translateY(-2px)'
    }
  }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ 
        p: 2, 
        bgcolor: `${color}.50`, 
        borderRadius: 2,
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        flexShrink: 0
      }}>
        <Icon sx={{ color: `${color}.main`, fontSize: 24 }} />
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 56 }}>
        <Typography variant="body2" color="text.secondary" sx={{ 
          fontSize: '0.875rem', 
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 0.75,
          lineHeight: 1
        }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ 
          fontWeight: 700,
          color: '#1a1a1a',
          lineHeight: 1,
          mb: sublabel ? 0.75 : 0
        }}>
          {value}
        </Typography>
        {sublabel && (
          <Typography variant="body2" color="text.secondary" sx={{ 
            fontSize: '0.85rem', 
            lineHeight: 1,
            fontWeight: 500
          }}>
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  </Card>
);

const NovaMatchResult = ({ result, analysisId, resumeContent, mustHaveSkills, onNewAssessment }) => {
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  if (!result || result.error) return null;

  // Debug logging removed

  // Validate and correct the scoring
  const validatedResult = validateScoring(result);

  // Note: Using backend-calculated scores directly for consistency

  const computeDisplayScores = () => {
    const expScore = Number(validatedResult?.scores?.experienceIndustryFit?.score ?? 0);
    const skillsScore = Number(validatedResult?.scores?.technicalSkillsCerts?.score ?? 0);
    const eduScore = Number(validatedResult?.scores?.educationCredentials?.score ?? 0);
    const commScore = Number(validatedResult?.scores?.communicationSoftSkills?.score ?? 0);
    const achScore = Number(validatedResult?.scores?.achievementQuality?.score ?? 0);

    // Use the backend-calculated overall score directly to ensure consistency
    const overallComputed = Number(validatedResult?.scores?.overall?.score ?? 0);

    return {
      expScore,
      skillsScore,
      eduScore,
      commScore,
      achScore,
      overallComputed
    };
  };

  const displayScores = computeDisplayScores();

  // Normalize verdict to consistent chip colors
  const getVerdictColor = (verdict) => {
    const v = String(verdict || '').toUpperCase();
    if (v.includes('STRONG') || v.includes('PROCEED') || v.includes('SCREENING')) return 'success';
    if (v.includes('MODERATE') || v === 'QUALIFIED' || v.includes('POTENTIAL')) return 'warning';
    if (v.includes('WEAK') || v.includes('NOT ALIGNED') || v.includes('REJECT')) return 'error';
    return 'default';
  };

  // Tab content components
  const OverviewTab = () => (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Score Breakdown */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Score Breakdown
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          alignItems: 'center', 
          py: { xs: 2, sm: 3, md: 4 }, 
          bgcolor: 'grey.50', 
          borderRadius: 2,
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: 2 }
        }}>
          <ScoreCircle 
            score={displayScores.expScore} 
            max={28} 
            label="Experience" 
            color="#3b82f6" 
          />
          <ScoreCircle 
            score={displayScores.skillsScore} 
            max={26} 
            label="Technical" 
            color="#8b5cf6" 
          />
          <ScoreCircle 
            score={displayScores.eduScore} 
            max={20} 
            label="Education" 
            color="#10b981" 
          />
          <ScoreCircle 
            score={displayScores.commScore} 
            max={16} 
            label="Communication" 
            color="#f59e0b" 
          />
          <ScoreCircle 
            score={displayScores.achScore} 
            max={10} 
            label="Achievements" 
            color="#ec4899" 
          />
        </Box>
      </Box>

      {/* Quick Stats Grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Grid item xs={12} sm={4}>
          {(() => {
            // Support both new and old field names for backward compatibility
            const matchedSkills = toArray(validatedResult?.keywordMatches?.mustHaveCovered || validatedResult?.keywordMatches?.criticalMatchedSkills);
            const missingSkills = toArray(validatedResult?.keywordMatches?.mustHaveMissing || validatedResult?.keywordMatches?.criticalMissingSkills);
            const matched = matchedSkills.length;
            const total = matched + missingSkills.length;
            const calculatedPercent = total > 0 ? Math.round((matched / total) * 100) : 0;
            return (
              <StatCard 
                icon={TargetIcon} 
                label="Required Skills Match" 
                value={`${matched} / ${total}`}
                sublabel={`${calculatedPercent}% coverage`}
                color="primary"
              />
            );
          })()}
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard 
            icon={StarIcon} 
            label="Certifications" 
            value={Array.isArray(validatedResult?.scores?.technicalSkillsCerts?.certifications?.found) ? 
              validatedResult.scores.technicalSkillsCerts.certifications.found.length : 0}
            sublabel={Array.isArray(validatedResult?.scores?.technicalSkillsCerts?.certifications?.found) && 
              validatedResult.scores.technicalSkillsCerts.certifications.found.length > 0 ? 
              validatedResult.scores.technicalSkillsCerts.certifications.found.join(', ') : 'None found'}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard 
            icon={TrendingUpIcon} 
            label="Salary Range" 
            value={validatedResult?.predictedSalary?.range || 'Not available'}
            sublabel={validatedResult?.predictedSalary?.confidence || 'Medium confidence'}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Skills Overview */}
      <Grid container spacing={3}>
        
        {/* Row 2: Required Skills and Nice to have skills (From Job Description) - Side by Side */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            p: 3, 
            height: '100%',
            border: '2px solid #ff9800',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(255, 152, 0, 0.15)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AssignmentIcon sx={{ color: '#f57c00', fontSize: 24, mr: 1.5 }} />
              <Typography variant="h6" fontWeight={800} sx={{ color: '#f57c00' }}>
                Required Skills (From Job Description)
              </Typography>
              <Box sx={{ 
                ml: 'auto', 
                px: 1.5, 
                py: 0.5, 
                bgcolor: '#f57c00', 
                color: 'white', 
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.8rem'
              }}>
                {toArray(validatedResult?.keywords?.jdMustHave).length}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {toArray(validatedResult?.keywords?.jdMustHave).map(skill => (
                  <Chip 
                    key={skill} 
                    label={skill} 
                    sx={{ 
                      bgcolor: '#f57c00',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      height: 28,
                      '&:hover': {
                        bgcolor: '#ef6c00',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(245, 124, 0, 0.3)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  />
                ))}
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            p: 3, 
            height: '100%',
            border: '2px solid #9c27b0',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ThumbUpIcon sx={{ color: '#7b1fa2', fontSize: 24, mr: 1.5 }} />
              <Typography variant="h6" fontWeight={800} sx={{ color: '#7b1fa2' }}>
                Nice to have skills (From Job Description)
              </Typography>
              <Box sx={{ 
                ml: 'auto', 
                px: 1.5, 
                py: 0.5, 
                bgcolor: '#7b1fa2', 
                color: 'white', 
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.8rem'
              }}>
                {toArray(validatedResult?.keywords?.jdNiceToHave).length}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 52 }}>
                {toArray(validatedResult?.keywords?.jdNiceToHave).length === 0 ? (
                  <Box sx={{ 
                    width: '100%', 
                    py: 2.5, 
                    px: 2, 
                    textAlign: 'center', 
                    bgcolor: 'rgba(123, 31, 162, 0.06)', 
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'rgba(123, 31, 162, 0.35)'
                  }}>
                    <ThumbUpIcon sx={{ color: '#7b1fa2', opacity: 0.5, fontSize: 28, mb: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#7b1fa2', fontWeight: 500, fontSize: '0.9rem' }}>
                      No nice-to-have skills listed in this job description
                    </Typography>
                  </Box>
                ) : (
                  toArray(validatedResult?.keywords?.jdNiceToHave).map(skill => (
                    <Chip 
                      key={skill} 
                      label={skill} 
                      sx={{ 
                        bgcolor: '#7b1fa2',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        height: 28,
                        '&:hover': {
                          bgcolor: '#6a1b9a',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 8px rgba(123, 31, 162, 0.3)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    />
                  ))
                )}
            </Box>
          </Card>
        </Grid>
        
        {/* Row 3: Skills Matched, Skills Missing, and Bonus Skills Matched - Three Columns */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            p: 3, 
            height: '100%',
            border: '2px solid #4caf50',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(76, 175, 80, 0.15)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 22, mr: 1.5 }} />
              <Typography variant="h6" fontWeight={800} sx={{ color: '#388e3c' }}>
                Skills Matched (From Candidate Resume)
              </Typography>
              <Box sx={{ 
                ml: 'auto', 
                px: 1.5, 
                py: 0.5, 
                bgcolor: '#388e3c', 
                color: 'white', 
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.8rem'
              }}>
                {toArray(validatedResult?.keywordMatches?.mustHaveCovered || validatedResult?.keywordMatches?.criticalMatchedSkills).length}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {toArray(validatedResult?.keywordMatches?.mustHaveCovered || validatedResult?.keywordMatches?.criticalMatchedSkills).map(skill => (
                  <Chip 
                    key={skill} 
                    label={skill} 
                    sx={{ 
                      bgcolor: '#4caf50',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      height: 28,
                      '&:hover': {
                        bgcolor: '#43a047',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  />
                ))}
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            p: 3, 
            height: '100%',
            border: '2px solid #f44336',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(244, 67, 54, 0.15)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CancelIcon sx={{ color: '#d32f2f', fontSize: 22, mr: 1.5 }} />
              <Typography variant="h6" fontWeight={800} sx={{ color: '#d32f2f' }}>
                Skills Missing (From Candidate Resume)
              </Typography>
              <Box sx={{ 
                ml: 'auto', 
                px: 1.5, 
                py: 0.5, 
                bgcolor: '#d32f2f', 
                color: 'white', 
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.8rem'
              }}>
                {toArray(validatedResult?.keywordMatches?.mustHaveMissing || validatedResult?.keywordMatches?.criticalMissingSkills).length}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 52 }}>
                {toArray(validatedResult?.keywordMatches?.mustHaveMissing || validatedResult?.keywordMatches?.criticalMissingSkills).length === 0 ? (
                  <Box sx={{ 
                    width: '100%', 
                    py: 2.5, 
                    px: 2, 
                    textAlign: 'center', 
                    bgcolor: 'rgba(211, 47, 47, 0.06)', 
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'rgba(244, 67, 54, 0.4)'
                  }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28, mb: 0.5 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem' }}>
                      All required skills found — none missing
                    </Typography>
                  </Box>
                ) : (
                  toArray(validatedResult?.keywordMatches?.mustHaveMissing || validatedResult?.keywordMatches?.criticalMissingSkills).map(skill => (
                    <Chip 
                      key={skill} 
                      label={skill} 
                      sx={{ 
                        bgcolor: '#f44336',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        height: 28,
                        '&:hover': {
                          bgcolor: '#e53935',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 8px rgba(244, 67, 54, 0.3)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    />
                  ))
                )}
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            p: 3, 
            height: '100%',
            border: '2px solid #ff9800',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(255, 152, 0, 0.15)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StarIcon sx={{ color: '#f57c00', fontSize: 22, mr: 1.5 }} />
              <Typography variant="h6" fontWeight={800} sx={{ color: '#f57c00' }}>
                Nice to have skills matched (From Candidate Resume)
              </Typography>
              <Box sx={{ 
                ml: 'auto', 
                px: 1.5, 
                py: 0.5, 
                bgcolor: '#f57c00', 
                color: 'white', 
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.8rem'
              }}>
                {toArray(validatedResult?.keywordMatches?.niceToHaveSkills || validatedResult?.keywordMatches?.niceToHaveMatched).length}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 52 }}>
                {toArray(validatedResult?.keywordMatches?.niceToHaveSkills || validatedResult?.keywordMatches?.niceToHaveMatched).length === 0 ? (
                  <Box sx={{ 
                    width: '100%', 
                    py: 2.5, 
                    px: 2, 
                    textAlign: 'center', 
                    bgcolor: 'rgba(255, 152, 0, 0.06)', 
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'rgba(255, 152, 0, 0.4)'
                  }}>
                    <StarIcon sx={{ color: '#ff9800', opacity: 0.6, fontSize: 28, mb: 0.5 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem' }}>
                      No nice-to-have skills matched yet
                    </Typography>
                  </Box>
                ) : (
                  toArray(validatedResult?.keywordMatches?.niceToHaveSkills || validatedResult?.keywordMatches?.niceToHaveMatched).map(skill => (
                    <Chip 
                      key={skill} 
                      label={skill} 
                      sx={{ 
                        bgcolor: '#ff9800',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        height: 28,
                        '&:hover': {
                          bgcolor: '#fb8c00',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 8px rgba(255, 152, 0, 0.3)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    />
                  ))
                )}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Work History */}
      <Card sx={{ 
        mt: 4,
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        border: '2px solid',
        borderColor: 'primary.main',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          p: 3, 
          bgcolor: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
          borderBottom: '2px solid',
          borderColor: 'primary.main'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight={800} color="primary.main">
                Professional Work History
              </Typography>
            </Box>
            <Chip
              label={`${(() => {
                const isValidCompany = (job) => {
                  if (!job || !job.company || job.company.trim() === '') return false;
                  const company = job.company.toLowerCase();
                  if (/@/.test(company)) return false;
                  if (/\d{3}[\s.-]?\d{3}[\s.-]?\d{4}/.test(company)) return false;
                  if (/\d{2,5}\s+[a-z]+\s+(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|way|ct|court)/i.test(company)) return false;
                  if (/\b\d{5}(-\d{4})?\b/.test(company)) return false;
                  if (company.length > 50 && !job.title) return false;
                  return true;
                };
                const count = ((validatedResult?.experienceTimeline || []).filter(isValidCompany)).length;
                return count;
              })()} ${(() => {
                const isValidCompany = (job) => {
                  if (!job || !job.company || job.company.trim() === '') return false;
                  const company = job.company.toLowerCase();
                  if (/@/.test(company)) return false;
                  if (/\d{3}[\s.-]?\d{3}[\s.-]?\d{4}/.test(company)) return false;
                  if (/\d{2,5}\s+[a-z]+\s+(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|way|ct|court)/i.test(company)) return false;
                  if (/\b\d{5}(-\d{4})?\b/.test(company)) return false;
                  if (company.length > 50 && !job.title) return false;
                  return true;
                };
                const count = ((validatedResult?.experienceTimeline || []).filter(isValidCompany)).length;
                return count === 1 ? 'Company' : 'Companies';
              })()}`}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.95rem',
                height: 36,
                px: 2
              }}
            />
          </Box>
        </Box>
        <Box sx={{ p: 3 }}>
          {(() => {
            // Filter out entries that contain personal information (email, phone, address patterns)
            const isValidCompany = (job) => {
              if (!job || !job.company || job.company.trim() === '') return false;
              const company = job.company.toLowerCase();
              
              // Exclude if contains email pattern
              if (/@/.test(company)) return false;
              
              // Exclude if contains phone number pattern (3+ digits with separators)
              if (/\d{3}[\s.-]?\d{3}[\s.-]?\d{4}/.test(company)) return false;
              
              // Exclude if contains street address pattern (number + street)
              if (/\d{2,5}\s+[a-z]+\s+(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|way|ct|court)/i.test(company)) return false;
              
              // Exclude if contains zip code pattern
              if (/\b\d{5}(-\d{4})?\b/.test(company)) return false;
              
              // Must have a meaningful title if company looks like personal info
              if (company.length > 50 && !job.title) return false;
              
              return true;
            };
            
            const validJobs = Array.isArray(validatedResult?.experienceTimeline) 
              ? validatedResult.experienceTimeline.filter(isValidCompany)
              : [];
            
            return validJobs.length > 0 ? (
              <Stack 
                spacing={1.5}
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 0.5,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                }}
              >
                {validJobs.slice(0, 5).map((job, idx) => {
                  const dateRange = [job.startDate, job.endDate].filter(Boolean).join(' - ');
                  return (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1.5,
                        p: 2,
                        bgcolor: 'rgba(25, 118, 210, 0.02)',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: 'rgba(25, 118, 210, 0.15)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(25, 118, 210, 0.06)',
                          borderColor: 'primary.main',
                          transform: 'translateX(4px)',
                          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)'
                        }
                      }}
                    >
                      <Box sx={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <BusinessIcon sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" color="text.primary" fontWeight={700} sx={{ lineHeight: 1.6, mb: 0.5 }}>
                          {job.company}
                        </Typography>
                        {job.title && (
                          <Typography variant="body2" color="text.primary" sx={{ display: 'block', lineHeight: 1.5, fontWeight: 500 }}>
                            {job.title}
                          </Typography>
                        )}
                        {dateRange && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, mt: 0.5 }}>
                            🗓️ {dateRange}
                          </Typography>
                        )}
                        {job.location && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                            📍 {job.location}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {validJobs.length > 5 && (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 2, 
                    px: 3,
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'primary.main'
                  }}>
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                      + {validJobs.length - 5} more {validJobs.length - 5 === 1 ? 'company' : 'companies'}
                    </Typography>
                  </Box>
                )}
              </Stack>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider'
              }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                <Typography variant="body1" color="text.secondary" fontWeight={600}>
                  No work history found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Work experience details not available in resume
                </Typography>
              </Box>
            );
          })()}
        </Box>
      </Card>
    </Box>
  );

  const TechnicalTab = () => {
    const skillsScore = displayScores.skillsScore;
    const skillsPercentage = Math.round((skillsScore / 26) * 100);
    // Support both new and old field names for backward compatibility
    const matchedSkills = toArray(validatedResult?.keywordMatches?.mustHaveCovered || validatedResult?.keywordMatches?.criticalMatchedSkills);
    const missingSkills = toArray(validatedResult?.keywordMatches?.mustHaveMissing || validatedResult?.keywordMatches?.criticalMissingSkills);
    const totalSkills = matchedSkills.length + missingSkills.length;
    // Calculate coverage percent based on actual counts for accuracy
    const coveragePercent = totalSkills > 0 ? Math.round((matchedSkills.length / totalSkills) * 100) : 0;
    
    return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Technical Overview Banner */}
        <Card 
          sx={{ 
            mb: { xs: 2, sm: 3 }, 
            p: { xs: 2, sm: 3 },
            bgcolor: '#f8f9fa',
            border: '2px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderRadius: 2
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', fontSize: { xs: '0.7rem', sm: '0.75rem' }, letterSpacing: 1 }}>
                  Required Skills Match
            </Typography>
                <Typography variant="h2" fontWeight={700} color="primary.main" sx={{ mb: 0.5, fontSize: { xs: '2.5rem', sm: '3rem' } }}>
                  {matchedSkills.length} / {totalSkills}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  {coveragePercent}% coverage
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
            <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight={600} color="text.primary">Technical Score</Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary">{skillsScore}/26 ({skillsPercentage}%)</Typography>
              </Box>
                  <Box 
                    sx={{ 
                      height: 12, 
                      bgcolor: '#e0e0e0', 
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{ 
                        height: '100%', 
                        width: `${skillsPercentage}%`,
                        bgcolor: '#1976d2',
                        borderRadius: 2,
                        transition: 'width 1s ease-in-out'
                      }} 
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {skillsScore >= 20 ? 'Strong technical foundation with comprehensive skill coverage across key technologies.' :
                     skillsScore >= 15 ? 'Good technical skill alignment with most core competencies demonstrated.' :
                     skillsScore >= 10 ? 'Basic technical skills present with opportunities for growth in critical areas.' :
                     'Limited technical skill coverage. Significant development needed in required technologies.'}
                </Typography>
              </Box>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Score Explanation Section */}
        {validatedResult?.scores?.technicalSkillsCerts?.reasoning && (
          <Card 
            sx={{ 
              mb: 3, 
              p: 3,
              bgcolor: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
              borderRadius: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CodeIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                💡 Score Explanation ({skillsScore}/26 Points)
              </Typography>
            </Box>
            
            <Box sx={{ 
              p: 2.5, 
              bgcolor: 'white',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light'
            }}>
              <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                Why {skillsScore}/26 Points?
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.8 }}>
                {validatedResult.scores.technicalSkillsCerts.reasoning}
              </Typography>
            </Box>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* Skills Assessment */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <TargetIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                Skills Assessment
              </Typography>
              <Stack spacing={2.5}>
                {/* Critical Match Skills */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: 'rgba(46, 125, 50, 0.05)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'success.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      ✓ Critical Skills Matched ({matchedSkills.length})
                  </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {matchedSkills.map(skill => (
                      <Chip 
                        key={skill} 
                        label={skill}
                        size="small"
                        sx={{
                          bgcolor: 'success.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.8rem'
                        }}
                      />
                    ))}
              </Box>
                </Box>

                {/* Critical Missing Skills */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: missingSkills.length > 0 ? 'rgba(211, 47, 47, 0.05)' : 'rgba(46, 125, 50, 0.05)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: missingSkills.length > 0 ? 'error.main' : 'success.light'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {missingSkills.length > 0 ? (
                      <WarningIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    ) : (
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    )}
                    <Typography 
                      variant="body2" 
                      fontWeight={700} 
                      color={missingSkills.length > 0 ? 'error.dark' : 'success.dark'} 
                      sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}
                    >
                      {missingSkills.length > 0 ? '⚠️ Critical Skills Missing' : '✓ All Skills Present'} ({missingSkills.length})
                  </Typography>
                  </Box>
                  {missingSkills.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {missingSkills.map(skill => (
                        <Chip 
                          key={skill} 
                          label={skill}
                          size="small"
                          sx={{
                            bgcolor: 'error.main',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.8rem'
                          }}
                        />
                      ))}
            </Box>
                  ) : (
                    <Typography variant="body2" color="success.dark" sx={{ fontWeight: 500 }}>
                      Excellent! All required skills are present in the candidate's profile.
                    </Typography>
                  )}
          </Box>

                {/* Bonus Skills */}
                {toArray(validatedResult?.keywordMatches?.niceToHaveSkills).length > 0 && (
                <Box 
                  sx={{ 
                    p: 2.5, 
                      bgcolor: 'rgba(237, 108, 2, 0.05)',
                    borderRadius: 2,
                    border: '1px solid',
                      borderColor: 'rgba(237, 108, 2, 0.3)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <StarIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        ⭐ Bonus Skills ({toArray(validatedResult?.keywordMatches?.niceToHaveSkills).length})
                  </Typography>
                    </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {toArray(validatedResult?.keywordMatches?.niceToHaveSkills).map(skill => (
                        <Chip 
                          key={skill} 
                          label={skill}
                          size="small"
                          sx={{
                            bgcolor: 'warning.main',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.8rem'
                          }}
                        />
                      ))}
            </Box>
          </Box>
                )}
            </Stack>
          </Card>
        </Grid>

          {/* Right Column - Certifications & Metrics */}
        <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <StarIcon sx={{ fontSize: 28, color: 'warning.main' }} />
                Credentials & Metrics
            </Typography>
            
            <Stack spacing={2.5}>
              {/* Technical Metrics Summary */}
              <Box 
                sx={{ 
                  p: 2.5, 
                  bgcolor: 'rgba(25, 118, 210, 0.05)',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TargetIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                    📊 Technical Metrics
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {matchedSkills.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Skills Matched
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="h4" fontWeight={700} color="primary.main">
                        {coveragePercent}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Coverage
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="h4" fontWeight={700} color={missingSkills.length > 0 ? 'error.main' : 'success.main'}>
                        {missingSkills.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Missing
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="h4" fontWeight={700} color="text.primary">
                        {skillsScore}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Score /26
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            
              {/* Certifications */}
              <Box 
                sx={{ 
                  p: 2.5, 
                  bgcolor: Array.isArray(validatedResult?.scores?.technicalSkillsCerts?.certifications?.found) && 
                           validatedResult.scores.technicalSkillsCerts.certifications.found.length > 0 ? 
                           'rgba(237, 108, 2, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: Array.isArray(validatedResult?.scores?.technicalSkillsCerts?.certifications?.found) && 
                              validatedResult.scores.technicalSkillsCerts.certifications.found.length > 0 ? 
                              'warning.main' : 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <StarIcon sx={{ 
                    color: Array.isArray(validatedResult?.scores?.technicalSkillsCerts?.certifications?.found) && 
                           validatedResult.scores.technicalSkillsCerts.certifications.found.length > 0 ? 
                           'warning.main' : 'text.secondary',
                    fontSize: 20 
                  }} />
                  <Typography 
                    variant="body2" 
                    fontWeight={700} 
                    color={Array.isArray(validatedResult?.scores?.technicalSkillsCerts?.certifications?.found) && 
                           validatedResult.scores.technicalSkillsCerts.certifications.found.length > 0 ? 
                           'warning.dark' : 'text.secondary'}
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}
                  >
                    🎓 Professional Certifications
                </Typography>
                </Box>
            {Array.isArray(validatedResult?.scores?.technicalSkillsCerts?.certifications?.found) && 
             validatedResult.scores.technicalSkillsCerts.certifications.found.length > 0 ? (
                <Stack spacing={1.5}>
                  {validatedResult.scores.technicalSkillsCerts.certifications.found.map((cert, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1.5,
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'warning.light'
                      }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 20, mt: 0.2, flexShrink: 0, color: 'warning.main' }} />
                        <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 600, color: 'text.primary' }}>
                          {cert}
                        </Typography>
                  </Box>
                ))}
              </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    No professional certifications found
                </Typography>
              </Box>
            )}
                </Box>
              </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
  };

  const ExperienceTab = () => {
    const expScore = displayScores.expScore;
    const expPercentage = Math.round((expScore / 28) * 100);
    const yearsExp = validatedResult?.candidateMetadata?.totalYearsExperience || 0;
    
    // Extract years requirement from JD
    const extractYearsFromJD = () => {
      const jdText = validatedResult?.inputData?.jobDescription || '';
      
      // Patterns to match: "5+ years", "3-5 years", "2+ years", "at least 3 years", etc.
      const patterns = [
        /(\d+)\+\s*(?:years?|yrs?)/i,                          // "5+ years"
        /(\d+)\s*-\s*(\d+)\s*(?:years?|yrs?)/i,               // "3-5 years"
        /(?:at least|minimum|min)\s*(\d+)\s*(?:years?|yrs?)/i, // "at least 3 years"
        /(\d+)\s*(?:or more|and above)\s*(?:years?|yrs?)/i    // "3 or more years"
      ];
      
      for (const pattern of patterns) {
        const match = jdText.match(pattern);
        if (match) {
          if (pattern.toString().includes('-')) {
            // Range format like "3-5"
            return `${match[1]}-${match[2]}`;
          } else if (jdText.substring(match.index, match.index + 20).includes('+')) {
            // Plus format like "5+"
            return `${match[1]}+`;
          } else {
            return `${match[1]}+`;
          }
        }
      }
      
      return null;
    };
    
    const jdYearsReq = extractYearsFromJD();
    
    return (
    <Box sx={{ p: 3 }}>
        {/* Experience Overview Banner */}
        <Card 
          sx={{ 
            mb: 3, 
            p: 3,
            bgcolor: '#f8f9fa',
            border: '2px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderRadius: 2
          }}
        >
          <Grid container spacing={3} alignItems="center">
            {/* Candidate Experience */}
            <Grid item xs={12} md={jdYearsReq ? 3 : 4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                  Candidate Experience
            </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h1" fontWeight={800} sx={{ 
                    color: '#1a1a1a',
                    fontSize: { xs: '3rem', sm: '3.5rem', md: '4rem' },
                    lineHeight: 1
                  }}>
                    {yearsExp}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                    {yearsExp === 1 ? 'Year' : 'Years'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            {/* JD Requirement (if available) */}
            {jdYearsReq && (
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                    JD Requirement
              </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h1" fontWeight={800} sx={{ 
                      color: '#e65100',
                      fontSize: { xs: '3rem', sm: '3.5rem', md: '4rem' },
                      lineHeight: 1
                    }}>
                      {jdYearsReq}
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                      Years
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12} md={jdYearsReq ? 6 : 8}>
            <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight={600} color="text.primary">Experience Score</Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary">{expScore}/28 ({expPercentage}%)</Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      height: 12, 
                      bgcolor: '#e0e0e0', 
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{ 
                        height: '100%', 
                        width: `${expPercentage}%`,
                        bgcolor: '#1976d2',
                        borderRadius: 2,
                        transition: 'width 1s ease-in-out'
                      }} 
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {yearsExp >= 5 ? 'Extensive experience demonstrating deep domain expertise and career progression.' :
                     yearsExp >= 2 ? 'Solid experience foundation with room for continued growth and development.' :
                     'Early career professional with foundational experience and high growth potential.'}
                </Typography>
              </Box>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Score Explanation Section */}
        {validatedResult?.scores?.experienceIndustryFit?.reasoning && (
          <Card 
            sx={{ 
              mb: 3, 
              p: 3,
              bgcolor: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
              borderRadius: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <BusinessIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                💡 Score Explanation ({expScore}/28 Points)
              </Typography>
            </Box>
            
            <Box sx={{ 
              p: 2.5, 
              bgcolor: 'white',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light'
            }}>
              <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                Why {expScore}/28 Points?
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.8 }}>
                {validatedResult.scores.experienceIndustryFit.reasoning}
              </Typography>
            </Box>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* Experience Details */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <BusinessIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                Key Experience Indicators
              </Typography>
              <Stack spacing={2.5}>
                {/* Years of Experience */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      ⏱️ Professional Experience
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                    {validatedResult?.candidateMetadata?.totalYearsExperience || 0} Years
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                    {validatedResult?.scores?.experienceIndustryFit?.details?.yearsExperience || 
                     'Experience level assessment based on work history'}
                  </Typography>
                </Box>

                {/* Role Match / Industry Alignment */}
                {validatedResult?.scores?.experienceIndustryFit?.details?.roleMatch && (
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(46, 125, 50, 0.05)',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        📊 Role & Experience Match
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip 
                        label={validatedResult.scores.experienceIndustryFit.details.roleMatch} 
                        color={
                          (validatedResult.scores.experienceIndustryFit.details.roleMatch || '').toLowerCase().includes('strong') ? 'success' :
                          (validatedResult.scores.experienceIndustryFit.details.roleMatch || '').toLowerCase().includes('direct') ? 'success' :
                          (validatedResult.scores.experienceIndustryFit.details.roleMatch || '').toLowerCase().includes('moderate') ? 'warning' :
                          (validatedResult.scores.experienceIndustryFit.details.roleMatch || '').toLowerCase().includes('partial') ? 'warning' :
                          'default'
                        }
                      sx={{ 
                        fontWeight: 700,
                        textTransform: 'capitalize',
                        fontSize: '0.875rem',
                        height: 32
                      }}
                />
              </Box>
                </Box>
                )}

                {/* Industries */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: 'rgba(237, 108, 2, 0.08)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'warning.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BusinessIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      🏢 Industries Worked In
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.primary" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Candidate has experience in:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(Array.isArray(validatedResult?.scores?.experienceIndustryFit?.details?.industries) ? 
                    validatedResult.scores.experienceIndustryFit.details.industries : ['Various industries']).map(industry => (
                      <Chip 
                        key={industry} 
                        label={industry}
                        size="small"
                        sx={{
                          bgcolor: 'warning.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.8rem'
                        }}
                      />
                  ))}
              </Box>
          </Box>
            </Stack>
          </Card>
        </Grid>

          {/* Right Column - Metrics & Client Skills */}
        <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <TrendingUpIcon sx={{ fontSize: 28, color: 'success.main' }} />
                Metrics & Skills
            </Typography>
            
              <Stack spacing={2.5}>
                {/* Experience Metrics */}
              <Box 
                sx={{ 
                  p: 2.5, 
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                  borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      📊 Experience Metrics
                </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="primary.main">
                          {expScore}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Score /28
                </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="success.main">
                          {expPercentage}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Percentage
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                          {yearsExp}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Years of Experience
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
            </Box>
              </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
  };


  const EducationTab = () => {
    const eduScore = displayScores.eduScore;
    const eduPercentage = Math.round((eduScore / 20) * 100);
    const degreeLevel = validatedResult?.scores?.educationCredentials?.details?.degreeLevel || 'Not specified';
    const fieldOfStudy = validatedResult?.scores?.educationCredentials?.details?.fieldOfStudy || 'Not specified';
    const jdEducationRequired = validatedResult?.scores?.educationCredentials?.details?.jdEducationRequired || 'Not mentioned';
    const additionalDegrees = toArray(validatedResult?.scores?.educationCredentials?.details?.additionalDegrees || []);
    const relevantCerts = toArray(validatedResult?.scores?.educationCredentials?.details?.relevantCertifications || []);
    
    return (
    <Box sx={{ p: 3 }}>
        {/* Education Overview Banner */}
        <Card 
          sx={{ 
            mb: 3, 
            p: 3,
            bgcolor: '#f8f9fa',
            border: '2px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderRadius: 2
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                  Education Level
            </Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                  {degreeLevel}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {fieldOfStudy}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight={600} color="text.primary">Education Score</Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary">{eduScore}/20 ({eduPercentage}%)</Typography>
              </Box>
                  <Box 
                    sx={{ 
                      height: 12, 
                      bgcolor: '#e0e0e0', 
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{ 
                        height: '100%', 
                        width: `${eduPercentage}%`,
                        bgcolor: '#1976d2',
                        borderRadius: 2,
                        transition: 'width 1s ease-in-out'
                      }} 
                    />
              </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {eduScore >= 16 ? 'Strong educational background with advanced credentials well-aligned to role requirements.' :
                     eduScore >= 12 ? 'Good educational foundation with relevant academic preparation for the position.' :
                     eduScore >= 8 ? 'Adequate educational background with foundational knowledge for the role.' :
                     'Limited educational credentials. Additional training or certifications may strengthen candidacy.'}
                  </Typography>
              </Box>
            </Stack>
        </Grid>
          </Grid>
        </Card>

        {/* Score Explanation Section */}
        {validatedResult?.scores?.educationCredentials?.reasoning && (
          <Card 
            sx={{ 
              mb: 3, 
              p: 3,
              bgcolor: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
              borderRadius: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AssignmentIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                💡 Score Explanation ({eduScore}/20 Points)
              </Typography>
            </Box>
            
            <Box sx={{ 
              p: 2.5, 
              bgcolor: 'white',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light'
            }}>
              <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                Why {eduScore}/20 Points?
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.8 }}>
                {validatedResult.scores.educationCredentials.reasoning}
              </Typography>
            </Box>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* Education Details */}
        <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <StarIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                Academic Credentials
            </Typography>
              <Stack spacing={2.5}>
                {/* Primary Degree */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <StarIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      🎓 Primary Degree
                  </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                    {degreeLevel}
                  </Typography>
                  <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                    {fieldOfStudy}
                  </Typography>
                </Box>

                {/* University & Year */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'rgba(25, 118, 210, 0.3)'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      🏛️ Institution & Graduation
                  </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    {validatedResult?.scores?.educationCredentials?.details?.universityName || 'Not specified'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Class of {validatedResult?.scores?.educationCredentials?.details?.graduationYear || 'N/A'}
                  </Typography>
                </Box>

                {/* JD Requirement */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: 'rgba(230, 81, 0, 0.05)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: jdEducationRequired === 'Not mentioned' ? 'rgba(230, 81, 0, 0.3)' : '#e65100'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <AssignmentIcon sx={{ color: jdEducationRequired === 'Not mentioned' ? 'text.secondary' : '#e65100', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color={jdEducationRequired === 'Not mentioned' ? 'text.secondary' : '#e65100'} sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      📋 Education Required (From JD)
                  </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ 
                    color: jdEducationRequired === 'Not mentioned' ? 'text.secondary' : '#e65100',
                    mb: 0.5
                  }}>
                    {jdEducationRequired}
                  </Typography>
                  {jdEducationRequired !== 'Not mentioned' && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Required by Job Description
                    </Typography>
                  )}
                </Box>

                {/* Additional Degrees */}
                {additionalDegrees.length > 0 && (
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(46, 125, 50, 0.05)',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        ✓ Additional Degrees ({additionalDegrees.length})
                    </Typography>
                    </Box>
                <Stack spacing={1.5}>
                      {additionalDegrees.map((degree, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1.5,
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'success.light'
                      }}
                    >
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
                            {degree}
                          </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
              </Stack>
            </Card>
          </Grid>

          {/* Right Column - Metrics & Certifications */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <TrendingUpIcon sx={{ fontSize: 28, color: 'success.main' }} />
                Credentials & Metrics
              </Typography>
              
              <Stack spacing={2.5}>
                {/* Education Metrics */}
              <Box 
                sx={{ 
                  p: 2.5, 
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                  borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AssignmentIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      📊 Education Metrics
                </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="primary.main">
                          {eduScore}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Score /20
                        </Typography>
                    </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="success.main">
                          {eduPercentage}%
              </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Percentage
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                          {additionalDegrees.length + 1}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {additionalDegrees.length + 1 === 1 ? 'Degree' : 'Degrees'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="warning.main">
                          {relevantCerts.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {relevantCerts.length === 1 ? 'Cert' : 'Certs'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
            </Box>

                {/* Academic Certifications */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: relevantCerts.length > 0 ? 'rgba(237, 108, 2, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: relevantCerts.length > 0 ? 'warning.main' : 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <StarIcon sx={{ 
                      color: relevantCerts.length > 0 ? 'warning.main' : 'text.secondary', 
                      fontSize: 20 
                    }} />
                    <Typography 
                      variant="body2" 
                      fontWeight={700} 
                      color={relevantCerts.length > 0 ? 'warning.dark' : 'text.secondary'} 
                      sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}
                    >
                      🎓 Academic Certifications
                    </Typography>
                  </Box>
                  {relevantCerts.length > 0 ? (
                  <Stack spacing={1.5}>
                      {relevantCerts.map((cert, idx) => (
                      <Box 
                        key={idx} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 1,
                    border: '1px solid',
                          borderColor: 'warning.light'
                  }}
                >
                          <CheckCircleIcon sx={{ fontSize: 20, mt: 0.2, flexShrink: 0, color: 'warning.main' }} />
                          <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 600, color: 'text.primary' }}>
                            {cert}
                  </Typography>
                      </Box>
                    ))}
                  </Stack>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        No additional certifications found
                </Typography>
              </Box>
            )}
              </Box>
              </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
  };

  const CommunicationTab = () => {
    const commScore = displayScores.commScore || 0;
    const commPercentage = Math.round((commScore / 16) * 100);
    const evidence = validatedResult?.scores?.communicationSoftSkills?.evidence || {};
    
    // Count skills based on examples presence (more reliable than just 'found === yes')
    // A skill is demonstrated if it has examples OR found === 'yes'
    const hasClientFacing = (evidence?.clientFacing?.examples?.length > 0) || (evidence?.clientFacing?.found === 'yes');
    const hasPresentations = (evidence?.presentations?.examples?.length > 0) || (evidence?.presentations?.found === 'yes');
    const hasLeadership = (evidence?.leadershipMentoring?.examples?.length > 0) || (evidence?.leadershipMentoring?.found === 'yes');
    const hasWritten = (evidence?.writtenCommunication?.examples?.length > 0) || (evidence?.writtenCommunication?.found === 'yes');
    
    // Calculate skills count from evidence
    const skillsCountFromEvidence = [hasClientFacing, hasPresentations, hasLeadership, hasWritten].filter(Boolean).length;
    
    // Estimate from score: 12/16 = 75% ≈ 3 skills, 16/16 = 100% = 4 skills, etc.
    const estimatedSkillsFromScore = Math.max(1, Math.min(4, Math.round((commScore / 16) * 4)));
    
    // Use evidence-based count if available, otherwise use score-based estimation
    // If evidence count is 0 but we have a score, trust the score estimation
    const finalSkillsCount = skillsCountFromEvidence > 0 ? skillsCountFromEvidence : estimatedSkillsFromScore;
    
    // Use the skill flags for display
    const clientFacing = hasClientFacing;
    const presentations = hasPresentations;
    const leadership = hasLeadership;
    const written = hasWritten;
    
    return (
    <Box sx={{ p: 3 }}>
        {/* Communication Overview Banner */}
        <Card 
          sx={{ 
            mb: 3, 
            p: 3,
            bgcolor: '#f8f9fa',
            border: '2px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderRadius: 2
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
              Communication Skills
            </Typography>
                <Typography variant="h2" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                  {finalSkillsCount}/4
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Key Skills Demonstrated
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
            <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight={600} color="text.primary">Communication Score</Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary">{commScore}/16 ({commPercentage}%)</Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      height: 12, 
                      bgcolor: '#e0e0e0', 
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{ 
                        height: '100%', 
                        width: `${commPercentage}%`,
                        bgcolor: '#1976d2',
                        borderRadius: 2,
                        transition: 'width 1s ease-in-out'
                      }} 
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {commScore >= 13 ? 'Strong communication and interpersonal skills with comprehensive soft skill demonstration.' :
                     commScore >= 10 ? 'Good communication capabilities with evidence of key soft skills for the role.' :
                     commScore >= 6 ? 'Basic communication skills present with opportunities to develop soft skill proficiency.' :
                     'Limited communication skills evidence. Soft skills development would strengthen candidacy.'}
                </Typography>
              </Box>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Score Explanation Section */}
        {validatedResult?.scores?.communicationSoftSkills?.reasoning && (
          <Card 
            sx={{ 
              mb: 3, 
              p: 3,
              bgcolor: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
              borderRadius: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ThumbUpIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                💡 Score Explanation ({commScore}/16 Points)
              </Typography>
            </Box>
            
            <Box sx={{ 
              p: 2.5, 
              bgcolor: 'white',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light'
            }}>
              <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                Why {commScore}/16 Points?
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.8 }}>
                {validatedResult.scores.communicationSoftSkills.reasoning}
              </Typography>
            </Box>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* Skills Breakdown */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <BusinessIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                Skills Assessment
                </Typography>
              <Stack spacing={2.5}>
                {/* Client-Facing */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: clientFacing ? 'rgba(46, 125, 50, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: clientFacing ? 'success.main' : 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      boxShadow: clientFacing ? '0 2px 8px rgba(46, 125, 50, 0.2)' : 'none'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%',
                      bgcolor: clientFacing ? 'success.main' : 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      flexShrink: 0
                    }}
                  >
                    {clientFacing ? <CheckCircleIcon sx={{ fontSize: 30 }} /> : <CancelIcon sx={{ fontSize: 30 }} />}
              </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} color={clientFacing ? 'success.dark' : 'text.secondary'} sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      👥 Client-Facing Experience
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                      {clientFacing ? '✓ Demonstrated' : '✗ Not Evident'}
                    </Typography>
                    {clientFacing && evidence?.clientFacing?.examples?.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5, fontStyle: 'italic' }}>
                        {evidence.clientFacing.examples[0]}
                      </Typography>
                    )}
              </Box>
                </Box>

                {/* Presentations */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: presentations ? 'rgba(46, 125, 50, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: presentations ? 'success.main' : 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      boxShadow: presentations ? '0 2px 8px rgba(46, 125, 50, 0.2)' : 'none'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%',
                      bgcolor: presentations ? 'success.main' : 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      flexShrink: 0
                    }}
                  >
                    {presentations ? <CheckCircleIcon sx={{ fontSize: 30 }} /> : <CancelIcon sx={{ fontSize: 30 }} />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} color={presentations ? 'success.dark' : 'text.secondary'} sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      🎤 Presentation Skills
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                      {presentations ? '✓ Demonstrated' : '✗ Not Evident'}
                    </Typography>
                    {presentations && evidence?.presentations?.examples?.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5, fontStyle: 'italic' }}>
                        {evidence.presentations.examples[0]}
                      </Typography>
                    )}
              </Box>
                </Box>

                {/* Leadership */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: leadership ? 'rgba(46, 125, 50, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: leadership ? 'success.main' : 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      boxShadow: leadership ? '0 2px 8px rgba(46, 125, 50, 0.2)' : 'none'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%',
                      bgcolor: leadership ? 'success.main' : 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      flexShrink: 0
                    }}
                  >
                    {leadership ? <CheckCircleIcon sx={{ fontSize: 30 }} /> : <CancelIcon sx={{ fontSize: 30 }} />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} color={leadership ? 'success.dark' : 'text.secondary'} sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      👨‍💼 Leadership & Mentoring
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                      {leadership ? '✓ Demonstrated' : '✗ Not Evident'}
                    </Typography>
                    {leadership && evidence?.leadershipMentoring?.examples?.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5, fontStyle: 'italic' }}>
                        {evidence.leadershipMentoring.examples[0]}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Written Communication */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: written ? 'rgba(46, 125, 50, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: written ? 'success.main' : 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      boxShadow: written ? '0 2px 8px rgba(46, 125, 50, 0.2)' : 'none'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%',
                      bgcolor: written ? 'success.main' : 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      flexShrink: 0
                    }}
                  >
                    {written ? <CheckCircleIcon sx={{ fontSize: 30 }} /> : <CancelIcon sx={{ fontSize: 30 }} />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} color={written ? 'success.dark' : 'text.secondary'} sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      ✍️ Written Communication
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                      {written ? '✓ Demonstrated' : '✗ Not Evident'}
                    </Typography>
                    {written && evidence?.writtenCommunication?.examples?.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5, fontStyle: 'italic' }}>
                        {evidence.writtenCommunication.examples[0]}
                      </Typography>
                    )}
                  </Box>
              </Box>
            </Stack>
          </Card>
        </Grid>

          {/* Evidence & Metrics */}
        <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <TrendingUpIcon sx={{ fontSize: 28, color: 'success.main' }} />
                Evidence & Metrics
            </Typography>
            
              <Stack spacing={2.5}>
                {/* Communication Metrics */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ThumbUpIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      📊 Communication Metrics
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="primary.main">
                          {commScore}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Score /16
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="success.main">
                          {commPercentage}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Percentage
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                          {finalSkillsCount}/4
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Key Skills Demonstrated
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

              {/* Examples with conditional rendering */}
                {evidence?.clientFacing?.examples && evidence.clientFacing.examples.length > 0 && (
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(46, 125, 50, 0.05)',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        👥 Client-Facing Examples
                    </Typography>
                    </Box>
                <Stack spacing={1.5}>
                      {evidence.clientFacing.examples.map((example, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1.5,
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'success.light'
                      }}
                    >
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
                            {example}
                          </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

                {evidence?.presentations?.examples && evidence.presentations.examples.length > 0 && (
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(46, 125, 50, 0.05)',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        🎤 Presentation Examples
                    </Typography>
                    </Box>
                <Stack spacing={1.5}>
                      {evidence.presentations.examples.map((example, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1.5,
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'success.light'
                      }}
                    >
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
                            {example}
                          </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

                {evidence?.leadershipMentoring?.examples && evidence.leadershipMentoring.examples.length > 0 && (
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(46, 125, 50, 0.05)',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        👨‍💼 Leadership Examples
                    </Typography>
                    </Box>
                <Stack spacing={1.5}>
                      {evidence.leadershipMentoring.examples.map((example, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1.5,
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'success.light'
                      }}
                    >
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
                            {example}
                          </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

                {/* Show message if no examples */}
                {(!evidence?.clientFacing?.examples || evidence.clientFacing.examples.length === 0) &&
                 (!evidence?.presentations?.examples || evidence.presentations.examples.length === 0) &&
                 (!evidence?.leadershipMentoring?.examples || evidence.leadershipMentoring.examples.length === 0) && (
                  <Box 
                    sx={{ 
                      p: 3, 
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                      No specific examples found in resume
                </Typography>
              </Box>
            )}
              </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
  };

  const AchievementsTab = () => {
    const achScore = displayScores.achScore;
    const achPercentage = Math.round((achScore / 10) * 100);
    const impactLevel = validatedResult?.scores?.achievementQuality?.impactLevel || 'Not specified';
    const quantifiedAchievements = toArray(validatedResult?.scores?.achievementQuality?.quantifiedAchievements || []);
    const qualitativeAchievements = toArray(validatedResult?.scores?.achievementQuality?.qualitativeAchievements || []);
    const totalAchievements = quantifiedAchievements.length + qualitativeAchievements.length;
    const achievementTypes = validatedResult?.scores?.achievementQuality?.achievementTypes || {};
    
    return (
    <Box sx={{ p: 3 }}>
        {/* Achievements Overview Banner */}
        <Card 
          sx={{ 
            mb: 3, 
            p: 3,
            bgcolor: '#f8f9fa',
            border: '2px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderRadius: 2
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                  Impact Level
            </Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 0.5, textTransform: 'capitalize' }}>
                  {impactLevel}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {totalAchievements} Total Achievements
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
            <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight={600} color="text.primary">Achievement Score</Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary">{achScore}/10 ({achPercentage}%)</Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      height: 12, 
                      bgcolor: '#e0e0e0', 
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{ 
                        height: '100%', 
                        width: `${achPercentage}%`,
                        bgcolor: '#1976d2',
                        borderRadius: 2,
                        transition: 'width 1s ease-in-out'
                      }} 
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {achScore >= 8 ? 'Strong achievement evidence with quantifiable results and measurable business impact.' :
                     achScore >= 6 ? 'Good achievement documentation with clear outcomes and contributions.' :
                     achScore >= 4 ? 'Basic achievement evidence present with opportunities to quantify impact further.' :
                     'Limited achievement documentation. Focus on quantifying results and demonstrating measurable impact.'}
                </Typography>
              </Box>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Score Explanation Section */}
        {validatedResult?.scores?.achievementQuality?.reasoning && (
          <Card 
            sx={{ 
              mb: 3, 
              p: 3,
              bgcolor: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
              borderRadius: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUpIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                💡 Score Explanation ({achScore}/10 Points)
              </Typography>
            </Box>
            
            <Box sx={{ 
              p: 2.5, 
              bgcolor: 'white',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light'
            }}>
              <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                Why {achScore}/10 Points?
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.8 }}>
                {validatedResult.scores.achievementQuality.reasoning}
              </Typography>
            </Box>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* Quantified Achievements */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <StarIcon sx={{ fontSize: 28, color: 'warning.main' }} />
                Quantified Achievements
              </Typography>
              
              <Stack spacing={2.5}>
              {quantifiedAchievements.length > 0 ? (
                <Box 
                  sx={{ 
                    p: 2.5, 
                      bgcolor: 'rgba(46, 125, 50, 0.05)',
                    borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        📊 Measurable Results ({quantifiedAchievements.length})
                </Typography>
              </Box>
                    <Stack spacing={1.5}>
                      {quantifiedAchievements.map((achievement, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: 1.5,
                            p: 1.5,
                            bgcolor: 'white',
                            borderRadius: 1,
                        border: '1px solid',
                            borderColor: 'success.light'
                      }}
                    >
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
                          {achievement}
                </Typography>
              </Box>
                      ))}
                    </Stack>
              </Box>
                ) : (
                  <Box 
                    sx={{ 
                      p: 3, 
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      textAlign: 'center'
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" fontWeight={600}>
                      No quantified achievements found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      No specific metrics or numbers identified in the resume
                    </Typography>
                  </Box>
                )}
            </Stack>

            {Object.keys(achievementTypes).some(type => achievementTypes[type]?.length > 0) && (
              <Box 
                sx={{ 
                  mt: 3,
                  p: 2.5, 
                  bgcolor: 'rgba(46, 125, 50, 0.05)',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'success.main'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body2" fontWeight={700} color="success.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                    📂 Achievement Categories
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {Object.entries(achievementTypes).map(([type, achievements]) => (
                    achievements && achievements.length > 0 && (
                      <Box 
                        key={type} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'success.light'
                        }}
                      >
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                          <strong>{type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {achievements.length}
                        </Typography>
                      </Box>
                    )
                  ))}
                </Stack>
              </Box>
            )}
          </Card>
        </Grid>

          {/* Metrics & Categories */}
        <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                p: 3, 
                height: '100%', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                <TrendingUpIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                Metrics & Categories
            </Typography>
            
              <Stack spacing={2.5}>
                {/* Achievement Metrics */}
                <Box 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <StarIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      📊 Achievement Metrics
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="primary.main">
                          {achScore}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Score /10
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="success.main">
                          {achPercentage}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Percentage
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="warning.main">
                          {quantifiedAchievements.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Quantified
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ textTransform: 'capitalize' }}>
                          {impactLevel === 'Not specified' ? 'N/A' : impactLevel.charAt(0).toUpperCase() + impactLevel.slice(1, 3)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Impact
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
            
              {/* Qualitative Achievements */}
              {qualitativeAchievements.length > 0 && (
                <Box 
                  sx={{ 
                    p: 2.5, 
                      bgcolor: 'rgba(237, 108, 2, 0.08)',
                    borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'warning.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <StarIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        ⭐ Leadership & Innovation ({qualitativeAchievements.length})
                  </Typography>
                    </Box>
                  <Stack spacing={1.5}>
                    {qualitativeAchievements.map((achievement, idx) => (
                      <Box 
                        key={idx} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'warning.light'
                        }}
                      >
                          <CheckCircleIcon sx={{ color: 'warning.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
                          {achievement}
                        </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

              </Stack>

              {/* Empty state */}
              {qualitativeAchievements.length === 0 && 
               !Object.keys(achievementTypes).some(type => achievementTypes[type]?.length > 0) && (
                <Box 
                  sx={{ 
                    p: 3, 
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    textAlign: 'center'
                  }}
                >
                  <StarIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" fontWeight={600}>
                    No additional achievements found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    No qualitative achievements or categories identified
                </Typography>
          </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
  };

  const DocumentAnalysisTab = () => {
    // Same Document Analysis as the modal: SkillHighlighting (side-by-side JD + resume with skill highlighting)
    const scanData = {
      ...result,
      resumeText: resumeContent || result?.resumeText || '',
      jobDescription: result?.jdText || result?.inputData?.jobDescription || '',
      resumeContent: resumeContent || result?.resumeText || '',
      keywordMatches: result?.keywordMatches || result?.analysisResults?.keywordMatches,
      keywords: result?.keywords || result?.analysisResults?.keywords,
      skillMatching: result?.skillMatching || result?.analysisResults?.skillMatching,
      analysisResults: result?.analysisResults || result
    };
    return (
      <Box sx={{ p: 0, minHeight: 400 }}>
        <SkillHighlighting scanData={scanData} />
      </Box>
    );
  };

  const HRVerdictTab = () => {
    const [hrQuestionsExpanded, setHrQuestionsExpanded] = useState(false);
    const [techQuestionsExpanded, setTechQuestionsExpanded] = useState(false);
    const allowedRiskLevels = ['Low', 'Medium', 'High'];
    const allowedConfidenceLevels = ['High', 'Medium', 'Low'];
    const allowedEducationStatus = ['PASS', 'FAIL', 'CAUTION'];

    const pickValue = (value, allowed, fallback) => {
      if (!value) return fallback;
      const normalized = String(value).trim().toLowerCase();
      const match = allowed.find(option => option.toLowerCase() === normalized);
      return match || fallback;
    };

    const riskPalette = {
      Low: { chipColor: 'success', borderColor: 'success.200', textColor: '#1a1a1a', bgColor: 'success.50' },
      Medium: { chipColor: 'warning', borderColor: 'warning.200', textColor: '#1a1a1a', bgColor: 'warning.50' },
      High: { chipColor: 'error', borderColor: 'error.200', textColor: '#1a1a1a', bgColor: 'error.50' }
    };

    const confidencePalette = {
      High: 'success',
      Medium: 'warning',
      Low: 'error'
    };

    const educationPalette = {
      PASS: 'success',
      CAUTION: 'warning',
      FAIL: 'error'
    };

    const getRiskStyles = (riskLevel) => riskPalette[riskLevel] || riskPalette.Medium;
    const getConfidenceColor = (confidence) => confidencePalette[confidence] || 'default';
    const getEducationColor = (status) => educationPalette[status] || 'default';

    const commitmentAssessment = validatedResult?.hrVerdict?.commitmentRiskAssessment || null;
    const technicalAssessment = validatedResult?.hrVerdict?.technicalRiskAssessment || null;

    const safeTrim = (value) => typeof value === 'string' ? value.trim() : '';

    // Clean up tags from commitment paragraph
    const cleanCommitmentParagraph = (text) => {
      if (!text) return '';
      return text
        .replace(/\b(?:PASS|FAIL|CAUTION):\s*/gi, '')
        .replace(/\[Risk:\s*(?:Low|Medium|High)\]/gi, '')
        .replace(/^\s*[:\-]\s*/, '')
        .trim();
    };

    const commitmentData = {
      paragraph: cleanCommitmentParagraph(commitmentAssessment?.paragraph || 'Fit & commitment risk assessment not available. Confirm location and education expectations directly with the candidate.'),
      riskLevel: pickValue(commitmentAssessment?.riskLevel, allowedRiskLevels, 'Medium'),
      confidence: pickValue(commitmentAssessment?.confidence, allowedConfidenceLevels, 'Low')
    };

    // Remove education paragraph - it's now in Fit & Commitment Risk
    const technicalParagraphs = Array.from(new Set([
      safeTrim(technicalAssessment?.skillsParagraph),
      safeTrim(technicalAssessment?.overallParagraph)
    ].filter(Boolean)));

    // Clean up any tags (PASS:, FAIL:, CAUTION:, etc.)
    const cleanParagraph = (text) => {
      if (!text) return '';
      return text
        .replace(/\b(?:PASS|FAIL|CAUTION):\s*/gi, '')
        .replace(/\[Risk:\s*(?:Low|Medium|High)\]/gi, '')
        .replace(/^\s*[:\-]\s*/, '')
        .trim();
    };

    const technicalCombinedParagraph = technicalParagraphs.length
      ? technicalParagraphs.map(cleanParagraph).filter(Boolean).join(' ')
      : 'Technical risk assessment not available. Confirm technical requirements directly with the candidate.';

    const technicalData = {
      paragraph: technicalCombinedParagraph,
      riskLevel: pickValue(technicalAssessment?.riskLevel, allowedRiskLevels, 'Medium'),
      confidence: pickValue(technicalAssessment?.confidence, allowedConfidenceLevels, 'Low')
    };

    const commitmentStyles = getRiskStyles(commitmentData.riskLevel);
    const technicalStyles = getRiskStyles(technicalData.riskLevel);
    
    return (
    <Box sx={{ p: 3 }}>
      {/* Final Score Card */}
      <Card 
        sx={{ 
          mb: 3, 
          p: 3,
          bgcolor: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
          border: '2px solid',
          borderColor: 'primary.main',
          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
          borderRadius: 3
        }}
      >
        <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ mb: 3 }}>
          📊 Final Assessment Score
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'center', px: 3, py: 6, bgcolor: 'white', borderRadius: 2, border: '2px solid', borderColor: 'primary.main', minHeight: '280px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="h1" fontWeight={700} color="primary.main" sx={{ fontSize: { xs: '3.5rem', md: '4.5rem' } }}>
                {displayScores.overallComputed}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
                Overall Score out of 100
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip 
                  label={validatedResult?.scores?.overall?.verdict || 'EVALUATING'}
                  color={
                    (validatedResult?.scores?.overall?.verdict || '').includes('EXCELLENT') ? 'success' :
                    (validatedResult?.scores?.overall?.verdict || '').includes('STRONG') ? 'success' :
                    (validatedResult?.scores?.overall?.verdict || '').includes('QUALIFIED') ? 'warning' :
                    'error'
                  }
                  sx={{ fontWeight: 700, fontSize: '1rem', height: 36, px: 2 }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight={600}>Experience</Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">{displayScores.expScore}/28</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight={600}>Technical Skills</Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">{displayScores.skillsScore}/26</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight={600}>Education</Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">{displayScores.eduScore}/20</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight={600}>Communication</Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">{displayScores.commScore}/16</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight={600}>Achievements</Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">{displayScores.achScore}/10</Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Improvement Suggestions */}
      {displayScores.overallComputed < 80 && (
        <Card sx={{ mb: 3, p: 3, bgcolor: 'rgba(237, 108, 2, 0.05)', border: '2px solid', borderColor: 'warning.main', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%',
              bgcolor: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TrendingUpIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="warning.dark">
              💡 Areas for Improvement
            </Typography>
          </Box>
          <Stack spacing={2}>
            {validatedResult?.keywordMatches?.mustHaveMissing && toArray(validatedResult.keywordMatches.mustHaveMissing).length > 0 && (
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                  Technical Skills Gap
                </Typography>
                <Typography variant="body2" color="text.primary">
                  Missing critical skills: {toArray(validatedResult.keywordMatches.mustHaveMissing).join(', ')}. 
                  Consider taking courses or gaining hands-on experience in these areas to strengthen your profile.
                </Typography>
              </Box>
            )}
            {displayScores.expScore < 20 && (
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                  Experience Level
                </Typography>
                <Typography variant="body2" color="text.primary">
                  Additional years of relevant professional experience would significantly strengthen your candidacy for this role.
                </Typography>
              </Box>
            )}
            {displayScores.commScore < 10 && (
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                  Communication Skills
                </Typography>
                <Typography variant="body2" color="text.primary">
                  Highlight more examples of client-facing work, presentations, leadership, and written communication in your resume to demonstrate soft skills.
                </Typography>
              </Box>
            )}
            {displayScores.achScore < 6 && (
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                  Quantifiable Achievements
                </Typography>
                <Typography variant="body2" color="text.primary">
                  Add more specific, measurable achievements with numbers, percentages, or metrics to demonstrate your impact.
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>
      )}

      <Box sx={{ 
        p: 3, 
        borderRadius: 2, 
        border: '2px solid',
        borderColor: getVerdictColor(validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict) === 'success' ? 'success.main' :
                     getVerdictColor(validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict) === 'warning' ? 'warning.main' : 'error.main',
        bgcolor: getVerdictColor(validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict) === 'success' ? 'success.50' :
                 getVerdictColor(validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict) === 'warning' ? 'warning.50' : 'error.50',
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700} sx={{ 
              color: getVerdictColor(validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict) === 'success' ? 'success.main' :
                     getVerdictColor(validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict) === 'warning' ? 'warning.main' : 'error.main'
            }}>
              {(() => {
                const verdict = validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict || 'EVALUATING';
                if (verdict === 'Reject') return 'Recommendation: Reject';
                if (verdict === 'Screening') return 'Recommendation: Screening';
                if (verdict === 'Strong Hire') return 'Recommendation: Strong Candidate';
                return verdict;
              })()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip 
                label={`Confidence: ${validatedResult?.hrVerdict?.confidence || 'Medium'}`}
                color={validatedResult?.hrVerdict?.confidence === 'High' ? 'success' : 
                       validatedResult?.hrVerdict?.confidence === 'Low' ? 'error' : 'warning'}
                size="small"
              />
              <Chip 
                label={`Priority: ${validatedResult?.hrVerdict?.priority || 'Normal'}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>
          {getVerdictColor(validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict) === 'error' && (
            <CancelIcon sx={{ color: 'error.main', fontSize: 48 }} />
          )}
        </Box>
        
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography 
            variant="h6" 
            fontWeight={700} 
            sx={{ 
              mb: 2.5, 
              fontSize: '1.25rem',
              color: '#1a1a1a',
              letterSpacing: '-0.01em'
            }}
          >
            Role Alignment Summary
          </Typography>
            {(() => {
              // Generate professional role alignment summary for client communication
              const candidateName = validatedResult?.candidateMetadata?.name || 'The candidate';
              const jobTitle = validatedResult?.inputData?.jobTitle || 'the position';
              const yearsExp = validatedResult?.candidateMetadata?.totalYearsExperience || 0;
              const matchedSkills = toArray(validatedResult?.keywordMatches?.mustHaveCovered || []);
              const missingSkills = toArray(validatedResult?.keywordMatches?.mustHaveMissing || []);
              const overallScore = validatedResult?.scores?.overall?.score || 0;
              const education = validatedResult?.candidateMetadata?.education || '';
              
            // Helper function to properly capitalize education
            const formatEducation = (edu) => {
              if (!edu) return '';
              
              // Split by common delimiters
              return edu.split(/([,;]|\sfrom\s|\sin\s|\sat\s)/gi)
                .map((part, idx, arr) => {
                  // Keep delimiters as-is
                  if (part.match(/^([,;]|\sfrom\s|\sin\s|\sat\s)$/i)) {
                    return part.toLowerCase();
                  }
                  
                  // Capitalize each word properly
                  return part.split(' ')
                    .map(word => {
                      const lower = word.toLowerCase();
                      // Keep acronyms and degrees uppercase
                      if (word.match(/^[A-Z\.]+$/)) return word.toUpperCase();
                      // Common degree abbreviations
                      if (lower === 'b.a.' || lower === 'b.s.' || lower === 'm.a.' || lower === 'm.s.' || 
                          lower === 'ph.d.' || lower === 'mba' || lower === 'ba' || lower === 'bs') {
                        return word.toUpperCase().replace(/([A-Z])\.([A-Z])\.$/, '$1.$2.');
                      }
                      // Capitalize first letter of each word except small words in the middle
                      if (idx > 0 && ['of', 'and', 'the', 'in', 'at', 'for'].includes(lower)) {
                        return lower;
                      }
                      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                    })
                    .join(' ');
                })
                .join('');
            };
            
            const formattedEducation = formatEducation(education);
            
            // Professional experience paragraph
            // Clean up job title for natural language
            const cleanJobTitle = jobTitle === 'the position' ? 'role' : `${jobTitle} role`;
            
            let experiencePara = '';
              if (yearsExp >= 5) {
              experiencePara = `${candidateName} brings ${yearsExp} years of solid professional experience in relevant roles, providing a strong foundation for this ${jobTitle}.`;
              } else if (yearsExp >= 2) {
              experiencePara = `With ${yearsExp} years of professional experience, ${candidateName} demonstrates a developing skillset for the ${jobTitle} role. While still building expertise, this foundation offers reasonable preparation for the position.`;
            } else if (yearsExp === 0 && formattedEducation) {
              experiencePara = `${candidateName} is a recent graduate with ${formattedEducation}. As an entry-level candidate, their educational background helps compensate for limited professional work experience in this field.`;
              } else {
              experiencePara = `${candidateName} currently has ${yearsExp} ${yearsExp === 1 ? 'year' : 'years'} of professional experience. Given the limited tenure, additional training and onboarding support would likely be needed to succeed in this ${cleanJobTitle}.`;
              }
              
            // Technical competency paragraph
            let technicalPara = '';
            if (matchedSkills.length > 0 && missingSkills.length > 0) {
                const topSkills = matchedSkills.slice(0, 3).join(', ');
              const criticalMissing = missingSkills.slice(0, 3).join(', ');
              technicalPara = `On the technical front, ${candidateName} shows strong capabilities in ${topSkills}, which align well with core role requirements. However, there are notable gaps in ${criticalMissing} that would benefit from targeted development or additional training to fully meet position demands.`;
            } else if (matchedSkills.length > 0) {
              const topSkills = matchedSkills.slice(0, 3).join(', ');
              technicalPara = `From a technical perspective, ${candidateName} demonstrates excellent alignment with role requirements, particularly in ${topSkills}. Their existing skill set provides a strong foundation for making immediate contributions to the team.`;
            } else if (missingSkills.length > 0) {
              const criticalMissing = missingSkills.slice(0, 3).join(', ');
              technicalPara = `The technical assessment reveals significant skill gaps in ${criticalMissing}, which are essential for success in this role. Bridging these competency gaps would require a substantial investment in training and development.`;
            }
            
            // Overall recommendation paragraph
            let recommendationPara = '';
              if (overallScore >= 80) {
              recommendationPara = `Overall, the assessment indicates strong alignment between ${candidateName} and the role requirements. We recommend moving forward to the next stage of the hiring process with high confidence in their potential for success and organizational fit.`;
              } else if (overallScore >= 60) {
              recommendationPara = `The overall assessment suggests ${candidateName} has adequate alignment with room for growth. We recommend considering them for an interview, with particular focus on discussing their development trajectory and how they plan to build capabilities in identified gap areas.`;
              } else {
              recommendationPara = `The evaluation reveals significant gaps between ${candidateName}'s current capabilities and role requirements. Moving forward would require considerable investment in training and development. We recommend carefully weighing your organization's capacity and timeline for supporting this level of candidate development.`;
            }
            
            return (
              <Stack spacing={2.5}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.9, 
                    color: '#2c2c2c',
                    fontSize: '1.05rem',
                    fontWeight: 400,
                    letterSpacing: '0.01em'
                  }}
                >
                  {experiencePara}
          </Typography>
                {technicalPara && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      lineHeight: 1.9, 
                      color: '#2c2c2c',
                      fontSize: '1.05rem',
                      fontWeight: 400,
                      letterSpacing: '0.01em'
                    }}
                  >
                    {technicalPara}
                  </Typography>
                )}
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.9, 
                    color: '#2c2c2c',
                    fontSize: '1.05rem',
                    fontWeight: 400,
                    letterSpacing: '0.01em'
                  }}
                >
                  {recommendationPara}
                </Typography>
              </Stack>
            );
          })()}
        </Box>
      </Box>

      {validatedResult?.interviewQuestions && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* HR Screening Questions */}
          <Grid item xs={12} md={6}>
            <Accordion 
              expanded={hrQuestionsExpanded} 
              onChange={() => setHrQuestionsExpanded(!hrQuestionsExpanded)}
              sx={{ 
                bgcolor: 'background.paper', 
                border: '2px solid', 
                borderColor: 'primary.main', 
                borderRadius: '8px !important',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:before': { display: 'none' },
                mb: 0
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  '& .MuiAccordionSummary-content': { my: 2 },
                  borderRadius: '8px'
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>🤝 HR Screening Questions</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {hrQuestionsExpanded ? 'Click to collapse' : 'Click to expand and view questions'}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                Friendly, light, non-technical questions to assess experience and comfort level
              </Typography>
              
              {/* Check if new structure with categories exists */}
              {validatedResult.interviewQuestions.profileBased || validatedResult.interviewQuestions.scenarioBased ? (
                <Stack spacing={2.5}>
                  {/* Profile-Based Questions */}
                  {validatedResult.interviewQuestions.profileBased && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'primary.main' }}>
                        📋 Experience & Self-Assessment
                      </Typography>
                      <Stack spacing={1}>
                        {toArray(validatedResult.interviewQuestions.profileBased).map((question, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: '20px' }}>
                              {i + 1}.
                            </Typography>
                            <Typography variant="body2">{question}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              ) : (
                /* Fallback for old flat structure */
                <Stack spacing={1}>
                  {toArray(validatedResult.interviewQuestions).map((question, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'primary.main', minWidth: '20px' }}>
                        {i + 1}.
                      </Typography>
                      <Typography variant="body2">{question}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Technical Evaluation Questions */}
          <Grid item xs={12} md={6}>
            <Accordion 
              expanded={techQuestionsExpanded} 
              onChange={() => setTechQuestionsExpanded(!techQuestionsExpanded)}
              sx={{ 
                bgcolor: 'background.paper', 
                border: '2px solid', 
                borderColor: 'secondary.main', 
                borderRadius: '8px !important',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:before': { display: 'none' },
                mb: 0
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  '& .MuiAccordionSummary-content': { my: 2 },
                  borderRadius: '8px'
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>⚙️ Technical Evaluation Questions</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {techQuestionsExpanded ? 'Click to collapse' : 'Click to expand and view questions'}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                Situational and technical questions to assess problem-solving and expertise
              </Typography>
              
              {/* Debug: Log interview questions structure */}
              {console.log('🔍 Interview Questions Debug:', {
                hasInterviewQuestions: !!validatedResult.interviewQuestions,
                structure: validatedResult.interviewQuestions,
                hasScenarioBased: !!validatedResult.interviewQuestions?.scenarioBased,
                hasProfileBased: !!validatedResult.interviewQuestions?.profileBased,
                isArray: Array.isArray(validatedResult.interviewQuestions)
              })}
              
              {/* Check if new structure with categories exists */}
              {validatedResult.interviewQuestions.scenarioBased ? (
                <Stack spacing={2.5}>
                  {/* Scenario-Based Questions */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'secondary.main' }}>
                      🎯 Situational & Technical Scenarios
                    </Typography>
                    <Stack spacing={1}>
                      {toArray(validatedResult.interviewQuestions.scenarioBased).map((question, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: '20px' }}>
                            {i + 1}.
                          </Typography>
                          <Typography variant="body2">{question}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              ) : (
                /* Fallback for old flat structure or missing data */
                <Stack spacing={1}>
                  {toArray(validatedResult.interviewQuestions).slice(5).map((question, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: '20px' }}>
                        {i + 1}.
                      </Typography>
                      <Typography variant="body2">{question}</Typography>
                    </Box>
                  ))}
                  {toArray(validatedResult.interviewQuestions).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No technical evaluation questions available for this analysis.
                    </Typography>
                  )}
                </Stack>
              )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'white',
              border: '2px solid',
              borderColor: 'warning.main',
              borderRadius: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                color={commitmentStyles.textColor}
                sx={{ fontSize: { xs: '1.05rem', sm: '1.1rem' } }}
              >
                📍 Fit & Commitment Risk
              </Typography>
              <Chip
                size="small"
                label={`Risk: ${commitmentData.riskLevel}`}
                color={commitmentStyles.chipColor}
                sx={{ fontWeight: 600 }}
              />
            </Box>
            {commitmentData.confidence && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  size="small"
                  label={`Confidence: ${commitmentData.confidence}`}
                  color={getConfidenceColor(commitmentData.confidence)}
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    borderWidth: 2,
                    bgcolor: `${getConfidenceColor(commitmentData.confidence)}.50`
                  }}
                />
              </Stack>
            )}
            <Typography
              variant="body2"
              color={commitmentStyles.textColor}
              sx={{ flex: 1, lineHeight: 1.65, fontSize: { xs: '0.95rem', sm: '1rem' }, fontWeight: 500 }}
            >
              {commitmentData.paragraph}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'white',
              border: '2px solid',
              borderColor: 'warning.main',
              borderRadius: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                color={technicalStyles.textColor}
                sx={{ fontSize: { xs: '1.05rem', sm: '1.1rem' } }}
              >
                🔧 Technical Risk Assessment
              </Typography>
              <Chip
                size="small"
                label={`Risk: ${technicalData.riskLevel}`}
                color={technicalStyles.chipColor}
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {technicalData.confidence && (
                <Chip
                  size="small"
                  label={`Confidence: ${technicalData.confidence}`}
                  color={getConfidenceColor(technicalData.confidence)}
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    borderWidth: 2,
                    bgcolor: `${getConfidenceColor(technicalData.confidence)}.50`
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="body2"
              color={technicalStyles.textColor}
              sx={{ lineHeight: 1.65, fontSize: { xs: '0.95rem', sm: '1rem' }, fontWeight: 500 }}
            >
              {technicalData.paragraph}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
  };

  // Print-friendly assessment report (use browser's Print → Save as PDF)
  const downloadAssessment = () => {
    const candidateName = validatedResult.candidateMetadata?.name || 'Candidate';
    
    // Use the same job title extraction logic as the history components
    const getJobTitle = () => {
      // This matches exactly what scanHistoryService.formatScanForDisplay uses
      return validatedResult.inputData?.jobTitle || 'Position';
    };
    
    const jobTitle = getJobTitle();
    const overallScore = displayScores.overallComputed;
    
    // Format education properly
    const formatEducationForDownload = (education) => {
      if (!education) return 'N/A';
      if (typeof education === 'object') {
        return `${education.degree || 'N/A'} in ${education.field || 'N/A'} from ${education.institution || 'N/A'}`;
      }
      return education;
    };


    // Format recommendations and interview questions
    const formatRecommendations = () => {
      const recs = toArray(validatedResult?.recommendations);
      if (recs.length === 0) return 'No specific recommendations available.';
      return recs.map(rec => `• ${rec}`).join('<br/>');
    };

    const formatInterviewQuestions = () => {
      // Handle structured interview questions (scenarioBased, profileBased)
      const interviewData = validatedResult?.interviewQuestions;
      if (!interviewData) return 'No interview questions generated.';
      
      // If it's an object with scenarioBased or profileBased arrays
      if (typeof interviewData === 'object' && !Array.isArray(interviewData)) {
        const allQuestions = [];
        
        // Collect scenario-based questions
        if (Array.isArray(interviewData.scenarioBased)) {
          allQuestions.push(...interviewData.scenarioBased);
        }
        
        // Collect profile-based questions
        if (Array.isArray(interviewData.profileBased)) {
          allQuestions.push(...interviewData.profileBased);
        }
        
        // If no structured questions, try to extract from other properties
        if (allQuestions.length === 0) {
          // Try to find any array properties
          for (const key in interviewData) {
            if (Array.isArray(interviewData[key])) {
              allQuestions.push(...interviewData[key]);
            }
          }
        }
        
        if (allQuestions.length === 0) return 'No interview questions generated.';
        
        // Format each question (handle objects and strings)
        return allQuestions.map(q => {
          if (typeof q === 'string') return `• ${q}`;
          if (typeof q === 'object' && q !== null) {
            // If question is an object, try to extract text
            return `• ${q.question || q.text || q.content || JSON.stringify(q)}`;
          }
          return `• ${String(q)}`;
        }).join('<br/>');
      }
      
      // Handle array format (legacy)
      if (Array.isArray(interviewData)) {
        if (interviewData.length === 0) return 'No interview questions generated.';
        return interviewData.map(q => {
          if (typeof q === 'string') return `• ${q}`;
          if (typeof q === 'object' && q !== null) {
            return `• ${q.question || q.text || q.content || JSON.stringify(q)}`;
          }
          return `• ${String(q)}`;
        }).join('<br/>');
      }
      
      return 'No interview questions generated.';
    };

    // Helper function to get risk color
    const getRiskColor = (riskLevel) => {
      if (riskLevel === 'Low') return '#2e7d32';
      if (riskLevel === 'High') return '#c62828';
      return '#ed6c02';
    };

    // Helper function to get risk background color
    const getRiskBgColor = (riskLevel) => {
      if (riskLevel === 'Low') return '#e8f5e9';
      if (riskLevel === 'High') return '#ffebee';
      return '#fff3e0';
    };

    // Helper function to get confidence color
    const getConfidenceColor = (confidence) => {
      if (confidence === 'High') return '#2e7d32';
      if (confidence === 'Low') return '#c62828';
      return '#ed6c02';
    };

    // Helper function to get confidence background color
    const getConfidenceBgColor = (confidence) => {
      if (confidence === 'High') return '#e8f5e9';
      if (confidence === 'Low') return '#ffebee';
      return '#fff3e0';
    };

    // Format commitment risk assessment
    const formatCommitmentRisk = () => {
      const assessment = validatedResult?.hrVerdict?.commitmentRiskAssessment;
      if (!assessment) {
        return {
          paragraph: 'Fit & commitment risk assessment not available. Confirm location and education expectations directly with the candidate.',
          riskLevel: 'Medium',
          confidence: null
        };
      }
      const paragraph = (assessment.paragraph || '')
        .replace(/\b(?:PASS|FAIL|CAUTION):\s*/gi, '')
        .replace(/\[Risk:\s*(?:Low|Medium|High)\]/gi, '')
        .replace(/^\s*[:\-]\s*/, '')
        .trim() || 'Fit & commitment risk assessment not available. Confirm location and education expectations directly with the candidate.';
      return {
        paragraph,
        riskLevel: assessment.riskLevel || 'Medium',
        confidence: assessment.confidence || null
      };
    };

    // Format technical risk assessment
    const formatTechnicalRisk = () => {
      const assessment = validatedResult?.hrVerdict?.technicalRiskAssessment;
      if (!assessment) {
        return {
          paragraph: 'Technical risk assessment not available. Confirm technical requirements directly with the candidate.',
          riskLevel: 'Medium',
          confidence: null
        };
      }
      const paragraphs = [
        assessment.skillsParagraph,
        assessment.overallParagraph
      ].filter(Boolean);
      
      if (paragraphs.length === 0) {
        return {
          paragraph: 'Technical risk assessment not available. Confirm technical requirements directly with the candidate.',
          riskLevel: 'Medium',
          confidence: null
        };
      }
      
      const paragraph = paragraphs.map(p => (p || '')
        .replace(/\b(?:PASS|FAIL|CAUTION):\s*/gi, '')
        .replace(/\[Risk:\s*(?:Low|Medium|High)\]/gi, '')
        .replace(/^\s*[:\-]\s*/, '')
        .trim()
      ).filter(Boolean).join(' ');
      
      return {
        paragraph,
        riskLevel: assessment.riskLevel || 'Medium',
        confidence: assessment.confidence || null
      };
    };

    const commitmentRisk = formatCommitmentRisk();
    const technicalRisk = formatTechnicalRisk();

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>NovaMatch: AI Recruitment Analysis - ${candidateName}</title>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif; 
            line-height: 1.6; 
            color: #333; 
            background: #ffffff;
            padding: 20px;
          }
          .container { max-width: 1200px; margin: 0 auto; background: white; border: 2px solid #e0e0e0; overflow: hidden; }
          .header { 
            background: #1976d2; 
            color: white; 
            padding: 40px; 
            text-align: center; 
            border-bottom: 4px solid #1565c0;
          }
          .header h1 { font-size: 2.2em; margin-bottom: 10px; font-weight: 700; letter-spacing: 0.5px; }
          .header h2 { font-size: 1.4em; margin-bottom: 20px; opacity: 0.9; font-weight: 500; }
          .score { font-size: 3.5em; font-weight: 800; margin: 20px 0; }
          .content { padding: 40px; }
          .section { 
            margin: 30px 0; 
            padding: 25px; 
            border: 1px solid #e0e0e0; 
            background: #ffffff;
            border-left: 4px solid #1976d2;
          }
          .section h3 { 
            color: #1976d2; 
            margin-bottom: 20px; 
            font-size: 1.3em; 
            font-weight: 700; 
            border-bottom: 2px solid #1976d2; 
            padding-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .section h4 { color: #555; margin: 15px 0 10px 0; font-size: 1.1em; font-weight: 600; }
          .metrics { 
            display: grid; 
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
            gap: 15px; 
            margin: 20px 0; 
          }
          .metric { 
            background: #f8f9fa; 
            padding: 15px; 
            border: 1px solid #e0e0e0; 
            text-align: center; 
            font-weight: 600; 
            border-left: 4px solid #1976d2;
          }
          .metric-value { font-size: 1.5em; color: #1976d2; }
          .candidate-info { 
            background: #f8f9fa; 
            padding: 25px; 
            border: 1px solid #e0e0e0;
          }
          .info-grid { 
            display: grid; 
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); 
            gap: 20px; 
            margin-top: 20px; 
          }
          .info-item { 
            background: white; 
            padding: 15px; 
            border: 1px solid #e0e0e0; 
            border-left: 4px solid #1976d2;
          }
          .info-label { font-weight: 600; color: #1976d2; margin-bottom: 5px; }
          .skills-container { margin: 20px 0; }
          .skills-list { 
            display: flex; 
            flex-wrap: wrap; 
            gap: 10px; 
            margin: 15px 0; 
          }
          .skill { 
            display: inline-block; 
            padding: 6px 12px; 
            border: 1px solid; 
            font-weight: 600; 
            font-size: 0.9em;
          }
          .matched-skill { background: #e8f5e8; color: #2e7d32; border-color: #2e7d32; }
          .missing-skill { background: #ffebee; color: #c62828; border-color: #c62828; }
          .jd-must-have-skill { background: #e3f2fd; color: #1976d2; border-color: #1976d2; }
          .jd-nice-to-have-skill { background: #fff3e0; color: #ed6c02; border-color: #ed6c02; }
          .nice-to-have-skill { background: #f3e5f5; color: #7b1fa2; border-color: #7b1fa2; }
          .skills-section-title { 
            color: #1976d2; 
            font-weight: 700; 
            margin: 20px 0 10px 0; 
            font-size: 1.1em;
            padding-bottom: 8px;
            border-bottom: 2px solid #1976d2;
          }
          .score-breakdown { 
            display: grid; 
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); 
            gap: 20px; 
            margin: 20px 0; 
          }
          .score-item { 
            background: white; 
            padding: 20px; 
            border: 1px solid #e0e0e0; 
            border-left: 4px solid #1976d2;
          }
          .score-title { font-weight: 700; color: #1976d2; margin-bottom: 10px; }
          .score-value { font-size: 1.3em; font-weight: 600; margin-bottom: 10px; }
          .score-reasoning { color: #666; font-style: italic; }
          .recommendations, .interview-questions, .verdict { 
            background: white; 
            padding: 25px; 
            border: 1px solid #e0e0e0;
          }
          .verdict { border-left: 4px solid #2e7d32; }
          .verdict-title { color: #2e7d32; font-weight: 700; font-size: 1.2em; margin-bottom: 15px; }
          .footer { 
            text-align: center; 
            margin-top: 40px; 
            padding: 20px; 
            color: #666; 
            font-size: 0.9em; 
            border-top: 1px solid #e0e0e0;
          }
          .page-break { page-break-before: always; }
          .report-date { font-size: 0.9em; opacity: 0.8; margin-top: 10px; }
          @media print {
            @page { size: A4; margin: 16mm; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            html, body { background: white !important; padding: 0 !important; }
            .container { border: 1px solid #000 !important; }
            .header { background: #1976d2 !important; }
            .section, .score-item, .recommendations, .interview-questions, .verdict, .candidate-info, .info-item {
              background: #ffffff !important; border: 1px solid #000 !important; }
            .metric { background: #ffffff !important; border: 1px solid #000 !important; }
            .matched-skill, .missing-skill { background: #ffffff !important; border: 1px solid #000 !important; }
            .section, .score-breakdown, .score-item, .metrics, .info-grid, .skills-list, .recommendations, .interview-questions, .verdict { page-break-inside: avoid; }
            .score { margin: 12px 0 !important; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
                    <h1>NovaMatch: AI Recruitment Analysis</h1>
                    <h2>${candidateName}</h2>
            <div class="score">${overallScore}/100</div>
            <p style="font-size: 1.1em; opacity: 0.9;">Comprehensive AI-Powered Recruitment Analysis</p>
                    <div class="report-date">Report Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>

          <div class="content">
            <!-- Candidate Information -->
            <div class="section">
              <h3>Candidate Information</h3>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Full Name</div>
                  <div>${validatedResult.candidateMetadata?.name || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Email Address</div>
                  <div>${validatedResult.candidateMetadata?.email || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Phone Number</div>
                  <div>${validatedResult.candidateMetadata?.phone || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Location</div>
                  <div>${(() => {
                    const smartLocation = getSmartLocation(
                      validatedResult.candidateMetadata,
                      validatedResult?.experienceTimeline || [],
                      validatedResult?.candidateMetadata?.education
                    );
                    return smartLocation.displayText;
                  })()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Education</div>
                  <div>${formatEducationForDownload(validatedResult.candidateMetadata?.education)}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Years of Experience</div>
                  <div>${validatedResult.candidateMetadata?.totalYearsExperience || 0} years</div>
                </div>
                        ${jobTitle && jobTitle !== 'Position' ? `
                        <div class="info-item">
                          <div class="info-label">Position Applied For</div>
                          <div>${jobTitle}</div>
                        </div>
                        ` : ''}
              </div>
            </div>

            <!-- Overall Assessment -->
            <div class="section">
              <h3>Role Alignment Summary</h3>
              <p style="font-size: 1.05em; line-height: 1.7; margin-bottom: 20px;">${(() => {
                const candidateName = validatedResult?.candidateMetadata?.name || 'The candidate';
                const yearsExp = validatedResult?.candidateMetadata?.totalYearsExperience || 0;
                const matchedSkills = toArray(validatedResult?.keywordMatches?.mustHaveCovered || []);
                const missingSkills = toArray(validatedResult?.keywordMatches?.mustHaveMissing || []);
                const overallScore = validatedResult?.scores?.overall?.score || 0;
                
                let experienceSummary = '';
                if (yearsExp >= 5) {
                  experienceSummary = `${candidateName} brings ${yearsExp} years of solid professional experience. `;
                } else if (yearsExp >= 2) {
                  experienceSummary = `With ${yearsExp} years of professional experience, ${candidateName} demonstrates a developing skillset. `;
                } else {
                  experienceSummary = `${candidateName} has ${yearsExp} ${yearsExp === 1 ? 'year' : 'years'} of professional experience. `;
                }
                
                let technicalSummary = '';
                if (matchedSkills.length > 0 && missingSkills.length > 0) {
                  const topSkills = matchedSkills.slice(0, 2).join(' and ');
                  const criticalMissing = missingSkills.slice(0, 2).join(' and ');
                  technicalSummary = `Shows strong capabilities in ${topSkills}, but has gaps in ${criticalMissing}. `;
                } else if (matchedSkills.length > 0) {
                  const topSkills = matchedSkills.slice(0, 2).join(' and ');
                  technicalSummary = `Demonstrates excellent alignment with role requirements, particularly in ${topSkills}. `;
                } else if (missingSkills.length > 0) {
                  const criticalMissing = missingSkills.slice(0, 2).join(' and ');
                  technicalSummary = `Has significant skill gaps in ${criticalMissing}. `;
                }
                
                let recommendation = '';
                if (overallScore >= 80) {
                  recommendation = `Strong alignment with high confidence for success.`;
                } else if (overallScore >= 60) {
                  recommendation = `Adequate alignment with room for growth.`;
                } else {
                  recommendation = `Significant gaps requiring considerable development investment.`;
                }
                
                return experienceSummary + technicalSummary + recommendation;
              })()}</p>
              <div class="metrics">
                <div class="metric">
                  <div class="metric-value">${(() => {
                    const matchedSkills = toArray(validatedResult?.keywordMatches?.mustHaveCovered || validatedResult?.keywordMatches?.criticalMatchedSkills);
                    const missingSkills = toArray(validatedResult?.keywordMatches?.mustHaveMissing || validatedResult?.keywordMatches?.criticalMissingSkills);
                    const total = matchedSkills.length + missingSkills.length;
                    const percent = total > 0 ? Math.round((matchedSkills.length / total) * 100) : 0;
                    return `${matchedSkills.length} / ${total}`;
                  })()}</div>
                  <div>Required Skills (${(() => {
                    const matchedSkills = toArray(validatedResult?.keywordMatches?.mustHaveCovered || validatedResult?.keywordMatches?.criticalMatchedSkills);
                    const missingSkills = toArray(validatedResult?.keywordMatches?.mustHaveMissing || validatedResult?.keywordMatches?.criticalMissingSkills);
                    const total = matchedSkills.length + missingSkills.length;
                    return total > 0 ? Math.round((matchedSkills.length / total) * 100) : 0;
                  })()}%)</div>
                </div>
                <div class="metric">
                  <div class="metric-value">${Math.round(((validatedResult?.scores?.experienceIndustryFit?.score || 0) / 28) * 100)}%</div>
                  <div>Experience Match</div>
                </div>
                <div class="metric">
                  <div class="metric-value">${Math.round(((validatedResult?.scores?.educationCredentials?.score || 0) / 20) * 100)}%</div>
                  <div>Education Fit</div>
                </div>
                <div class="metric">
                  <div class="metric-value">${Math.round(((validatedResult?.scores?.technicalSkillsCerts?.score || 0) / 26) * 100)}%</div>
                  <div>Technical Skills</div>
                </div>
              </div>
            </div>

            <!-- Detailed Scoring Breakdown -->
            <div class="section">
              <h3>Detailed Scoring Breakdown</h3>
              <div class="score-breakdown">
                <div class="score-item">
                  <div class="score-title">Experience & Industry Fit</div>
                  <div class="score-value">${displayScores.expScore}/28 points</div>
                  <div class="score-reasoning">${validatedResult?.scores?.experienceIndustryFit?.reasoning || 'Experience assessment based on years, role relevance, and industry alignment.'}</div>
                </div>
                <div class="score-item">
                  <div class="score-title">Technical Skills & Certifications</div>
                  <div class="score-value">${displayScores.skillsScore}/26 points</div>
                  <div class="score-reasoning">${validatedResult?.scores?.technicalSkillsCerts?.reasoning || 'Technical skills assessment based on required technology proficiency and certifications.'}</div>
                </div>
                <div class="score-item">
                  <div class="score-title">Education & Credentials</div>
                  <div class="score-value">${displayScores.eduScore}/20 points</div>
                  <div class="score-reasoning">${validatedResult?.scores?.educationCredentials?.reasoning || 'Education requirements assessment based on degree level and field relevance.'}</div>
                </div>
                <div class="score-item">
                  <div class="score-title">Communication & Soft Skills</div>
                  <div class="score-value">${displayScores.commScore}/16 points</div>
                  <div class="score-reasoning">${validatedResult?.scores?.communicationSoftSkills?.reasoning || 'Communication skills assessment based on client-facing experience and collaboration abilities.'}</div>
                </div>
                <div class="score-item">
                  <div class="score-title">Achievement Quality</div>
                  <div class="score-value">${displayScores.achScore}/10 points</div>
                  <div class="score-reasoning">${validatedResult?.scores?.achievementQuality?.reasoning || 'Impact and results assessment based on quantifiable achievements and leadership.'}</div>
                </div>
              </div>
            </div>

            <!-- Skills Analysis -->
            <div class="section">
              <h3 style="color: #1976d2; border-bottom: 3px solid #1976d2; padding-bottom: 10px;">Skills Analysis</h3>
              <div class="skills-container">
                <h4 class="skills-section-title" style="color: #1976d2;">Required Skills (From Job Description) - ${toArray(validatedResult?.keywords?.jdMustHave).length} identified</h4>
                <div class="skills-list">
                  ${toArray(validatedResult?.keywords?.jdMustHave).map(skill => 
                    `<span class="skill jd-must-have-skill">${skill}</span>`
                  ).join('')}
                </div>
                <h4 class="skills-section-title" style="color: #ed6c02;">Nice to have skills (From Job Description) - ${toArray(validatedResult?.keywords?.jdNiceToHave).length} identified</h4>
                <div class="skills-list">
                  ${toArray(validatedResult?.keywords?.jdNiceToHave).map(skill => 
                    `<span class="skill jd-nice-to-have-skill">${skill}</span>`
                  ).join('')}
                </div>
                <h4 class="skills-section-title" style="color: #2e7d32;">Skills Matched (From Candidate Resume) - ${toArray(validatedResult?.keywordMatches?.criticalMatchedSkills || validatedResult?.keywordMatches?.mustHaveCovered).length} found</h4>
                <div class="skills-list">
                  ${toArray(validatedResult?.keywordMatches?.criticalMatchedSkills || validatedResult?.keywordMatches?.mustHaveCovered).map(skill => 
                    `<span class="skill matched-skill">${skill}</span>`
                  ).join('')}
                </div>
                <h4 class="skills-section-title" style="color: #c62828;">Skills Missing (From Candidate Resume) - ${toArray(validatedResult?.keywordMatches?.criticalMissingSkills || validatedResult?.keywordMatches?.mustHaveMissing).length} found</h4>
                <div class="skills-list">
                  ${toArray(validatedResult?.keywordMatches?.criticalMissingSkills || validatedResult?.keywordMatches?.mustHaveMissing).map(skill => 
                    `<span class="skill missing-skill">${skill}</span>`
                  ).join('')}
                </div>
                <h4 class="skills-section-title" style="color: #7b1fa2;">Nice to have skills matched (From Candidate Resume) - ${toArray(validatedResult?.keywordMatches?.niceToHaveSkills || validatedResult?.keywordMatches?.niceToHaveMatched).length} found</h4>
                <div class="skills-list">
                  ${toArray(validatedResult?.keywordMatches?.niceToHaveSkills || validatedResult?.keywordMatches?.niceToHaveMatched).map(skill => 
                    `<span class="skill nice-to-have-skill">${skill}</span>`
                  ).join('')}
                </div>
              </div>
            </div>

            <!-- Recommendations -->
            <div class="section recommendations">
              <h3>Recommendations for Recruiter</h3>
              <div style="margin-top: 15px;">${formatRecommendations()}</div>
            </div>

            <!-- Interview Questions -->
            <div class="section interview-questions">
              <h3>Suggested Interview Questions</h3>
              <div style="margin-top: 15px;">${formatInterviewQuestions()}</div>
            </div>

            <!-- Risk Assessments -->
            <div class="section">
              <h3>Risk Assessment</h3>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); gap: 20px; margin-top: 20px;">
                <!-- Fit & Commitment Risk Assessment -->
                <div style="padding: 20px; border: 2px solid ${getRiskColor(commitmentRisk.riskLevel)}; border-radius: 8px; background: white;">
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 15px;">
                    <h4 style="color: ${getRiskColor(commitmentRisk.riskLevel)}; margin: 0; font-size: 1.1em; font-weight: 700;">📍 Fit & Commitment Risk</h4>
                    <span style="padding: 4px 12px; background: ${getRiskBgColor(commitmentRisk.riskLevel)}; color: ${getRiskColor(commitmentRisk.riskLevel)}; border: 1px solid ${getRiskColor(commitmentRisk.riskLevel)}; border-radius: 4px; font-size: 0.85em; font-weight: 600;">
                      Risk: ${commitmentRisk.riskLevel}
                    </span>
                  </div>
                  ${commitmentRisk.confidence ? `
                    <div style="margin-bottom: 15px;">
                      <span style="padding: 4px 12px; background: ${getConfidenceBgColor(commitmentRisk.confidence)}; color: ${getConfidenceColor(commitmentRisk.confidence)}; border: 1px solid ${getConfidenceColor(commitmentRisk.confidence)}; border-radius: 4px; font-size: 0.85em; font-weight: 600; display: inline-block;">
                        Confidence: ${commitmentRisk.confidence}
                      </span>
                    </div>
                  ` : ''}
                  <p style="line-height: 1.65; color: #333; margin: 0;">
                    ${commitmentRisk.paragraph}
                  </p>
                </div>

                <!-- Technical Risk Assessment -->
                <div style="padding: 20px; border: 2px solid ${getRiskColor(technicalRisk.riskLevel)}; border-radius: 8px; background: white;">
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 15px;">
                    <h4 style="color: ${getRiskColor(technicalRisk.riskLevel)}; margin: 0; font-size: 1.1em; font-weight: 700;">🔧 Technical Risk Assessment</h4>
                    <span style="padding: 4px 12px; background: ${getRiskBgColor(technicalRisk.riskLevel)}; color: ${getRiskColor(technicalRisk.riskLevel)}; border: 1px solid ${getRiskColor(technicalRisk.riskLevel)}; border-radius: 4px; font-size: 0.85em; font-weight: 600;">
                      Risk: ${technicalRisk.riskLevel}
                    </span>
                  </div>
                  ${technicalRisk.confidence ? `
                    <div style="margin-bottom: 15px;">
                      <span style="padding: 4px 12px; background: ${getConfidenceBgColor(technicalRisk.confidence)}; color: ${getConfidenceColor(technicalRisk.confidence)}; border: 1px solid ${getConfidenceColor(technicalRisk.confidence)}; border-radius: 4px; font-size: 0.85em; font-weight: 600; display: inline-block;">
                        Confidence: ${technicalRisk.confidence}
                      </span>
                    </div>
                  ` : ''}
                  <p style="line-height: 1.65; color: #333; margin: 0;">
                    ${technicalRisk.paragraph}
                  </p>
                </div>
              </div>
            </div>

            <!-- HR Final Verdict -->
            <div class="section verdict">
              <h3>HR Final Verdict</h3>
              <div class="verdict-title">Decision: ${validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict || 'EVALUATING'}</div>
              <div style="margin-top: 15px; font-size: 1.1em;">${validatedResult?.scores?.overall?.reasoning || 'Comprehensive assessment completed. Review all sections above for detailed analysis.'}</div>
            </div>
          </div>

          <div class="footer">
            <p><strong>Report Generated:</strong> ${new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} at ${new Date().toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}</p>
            <p style="margin-top: 10px; font-size: 0.8em; color: ${colors.neutral};">
              This report was generated by NovaMatch AI Assessment Engine
            </p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Use native print dialog for reliable PDF (works across browsers/OS)
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        // Popup blocked, fallback to HTML download
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = jobTitle && jobTitle !== 'Position' 
          ? `Complete_Assessment_${candidateName.replace(/\s+/g, '_')}_${jobTitle.replace(/\s+/g, '_')}_${overallScore}.html`
          : `Complete_Assessment_${candidateName.replace(/\s+/g, '_')}_${overallScore}.html`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return;
      }
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      // Ensure styles render before printing
      printWindow.focus();
      setTimeout(() => {
        try {
          printWindow.print();
        } finally {
          // Close after print in most browsers; user can cancel to keep window
          printWindow.close();
        }
      }, 400);
    } catch (e) {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = jobTitle && jobTitle !== 'Position' 
        ? `Complete_Assessment_${candidateName.replace(/\s+/g, '_')}_${jobTitle.replace(/\s+/g, '_')}_${overallScore}.html`
        : `Complete_Assessment_${candidateName.replace(/\s+/g, '_')}_${overallScore}.html`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };


  const mainContent = (
    <Box className="analysis-content" sx={{ 
      p: { xs: 2, sm: 3, md: 5 }, 
      maxWidth: '100%', 
      width: '100%',
      overflowX: 'hidden',
      overflowY: 'visible',
      boxSizing: 'border-box',
      minHeight: '100vh',
      height: 'fit-content',
      fontFamily: baseFont,
      bgcolor: '#f5f7fa',
      '& *': { fontFamily: baseFont }
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        mb: { xs: 3, sm: 4 },
        px: { xs: 1, sm: 2, md: 3 },
        py: 3,
        bgcolor: 'white',
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        border: '2px solid',
        borderColor: 'primary.main',
        maxWidth: '1000px',
        mx: 'auto'
      }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ 
            fontSize: '2.25rem',
            letterSpacing: '-0.02em'
          }}>
            NovaMatch
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ 
            fontSize: '1rem',
            fontWeight: 500
          }}>
            AI-Powered Resume Analysis
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
              <Tooltip title="Download Report" arrow>
                <IconButton
            onClick={downloadAssessment}
                  size="large"
                sx={{
                    color: 'primary.main',
              bgcolor: 'primary.50',
              width: { xs: 44, sm: 48 },
              height: { xs: 44, sm: 48 },
              '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  <DownloadIcon fontSize="large" />
                </IconButton>
              </Tooltip>
          
              <Button
                onClick={() => onNewAssessment ? onNewAssessment() : navigate('/')}
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.25, sm: 1.5 },
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  minHeight: 48,
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
                  }
                }}
              >
                New Assessment
              </Button>
        </Box>
          </Box>

      {/* Candidate Header */}
      {validatedResult.candidateMetadata && (
        <Card sx={{ 
          mb: { xs: 3, sm: 4 }, 
          borderRadius: { xs: 2, sm: 3, md: 4 }, 
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          background: 'white',
          overflow: 'hidden',
          position: 'relative',
          maxWidth: '1000px',
          mx: { xs: 0, sm: 'auto' },
          border: 'none',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '5px',
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)'
          }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, gap: { xs: 2, sm: 3 }, width: '100%' }}>
                <Avatar sx={{ 
                  width: { xs: 64, sm: 72 }, 
                  height: { xs: 64, sm: 72 }, 
                  bgcolor: 'primary.main',
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  border: '3px solid white'
                }}>
                  {validatedResult.candidateMetadata.name ? 
                    validatedResult.candidateMetadata.name.split(' ').map(n => n[0]).join('') : 
                    'N/A'
                  }
                </Avatar>
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, width: '100%' }}>
                  <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {validatedResult.candidateMetadata.name || 'Name Not Available'}
          </Typography>
                  <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600, opacity: 0.8, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {formatEducation(validatedResult.candidateMetadata.education)}
                  </Typography>
                  
                  {/* University Information */}
                  {(() => {
                    const { university, location: universityLocation } = extractUniversityInfo(validatedResult.candidateMetadata.education);
                    if (university) {
                      return (
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500, mt: 1 }}>
                          🎓 {university}{universityLocation ? `, ${universityLocation}` : ''}
                        </Typography>
                      );
                    }
                    return null;
                  })()}
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 3, 
                    mt: 3,
                    p: 2,
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'rgba(25, 118, 210, 0.1)'
                  }}>
                {validatedResult.candidateMetadata.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EmailOutlinedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                      {validatedResult.candidateMetadata.email}
          </Typography>
                  </Box>
                )}
                {validatedResult.candidateMetadata.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PhoneIphoneOutlinedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                      {validatedResult.candidateMetadata.phone}
          </Typography>
                  </Box>
                )}
                {(() => {
                  // Use smart location extraction with priority system
                  const smartLocation = getSmartLocation(
                    validatedResult.candidateMetadata,
                    validatedResult?.experienceTimeline || [],
                    validatedResult?.candidateMetadata?.education
                  );
                  
                  if (smartLocation.location) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationOnOutlinedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                          {smartLocation.displayText}
                        </Typography>
                      </Box>
                    );
                  } else {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationOnOutlinedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                        <Typography variant="body1" color="text.primary" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                          {smartLocation.displayText}
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                                Location Not Available
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                                <strong>For HR:</strong> Check these sources for location:
                              </Typography>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Personal Details:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  {validatedResult.candidateMetadata?.location || 'No location in personal details'}
                                </Typography>
                              </Box>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Latest Job Experience:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  {(validatedResult?.experienceTimeline || []).length > 0 
                                    ? `"${validatedResult.experienceTimeline[0]?.company}" - ${validatedResult.experienceTimeline[0]?.location || 'No location listed'}`
                                    : 'No job experience found'
                                  }
                                </Typography>
                              </Box>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Education:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  {validatedResult?.candidateMetadata?.education || 'No education details available'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • LinkedIn Profile:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Check candidate's LinkedIn for current location
                                </Typography>
                              </Box>
                            </Box>
                          }
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'rgba(0, 0, 0, 0.9)',
                                maxWidth: 350,
                                fontSize: '0.8rem'
                              }
                            }
                          }}
                        >
                          <IconButton size="small" sx={{ color: 'warning.main', p: 0.5 }}>
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  }
                })()}
                  </Box>
                  
                  {/* Recent Job Location */}
                  {(() => {
                    const recentJobLocation = extractRecentJobLocation(validatedResult?.experienceTimeline || []);
                    if (recentJobLocation) {
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          mt: 2,
                          p: 2,
                          bgcolor: 'rgba(76, 175, 80, 0.05)',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'rgba(76, 175, 80, 0.1)'
                        }}>
                          <BusinessIcon sx={{ color: 'success.main', fontSize: 18 }} />
                          <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                            Most Recent Job: {recentJobLocation}
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Location Inconsistency Warning */}
                  {(() => {
                    const inconsistencyCheck = checkLocationInconsistencies(
                      validatedResult.candidateMetadata,
                      validatedResult?.experienceTimeline || [],
                      validatedResult?.candidateMetadata?.education
                    );
                    
                    if (inconsistencyCheck.hasInconsistencies) {
                      // Group locations by region for better display
                      const locationsByRegion = {};
                      inconsistencyCheck.locations.forEach(loc => {
                        const state = extractStateRegion(loc.location);
                        const regionKey = state || 'Unknown Region';
                        if (!locationsByRegion[regionKey]) {
                          locationsByRegion[regionKey] = [];
                        }
                        locationsByRegion[regionKey].push(loc);
                      });
                      
                      // Only show warning if there are actually different regions
                      const regionKeys = Object.keys(locationsByRegion);
                      if (regionKeys.length <= 1) {
                        return null; // Don't show warning if all locations are in same region
                      }
                      
                      return (
                        <Box sx={{ 
                          mt: 2,
                          p: 2,
                          bgcolor: 'rgba(255, 152, 0, 0.08)',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'rgba(255, 152, 0, 0.25)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <WarningIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                            <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                              Location Inconsistency Detected
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                            Candidate has locations in different states/regions:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                            {Object.entries(locationsByRegion).map(([region, locations]) => (
                              <Box key={region} sx={{ 
                                minWidth: 'fit-content',
                                bgcolor: 'rgba(255, 152, 0, 0.1)',
                                borderRadius: 1.5,
                                p: 1.5,
                                border: '1px solid rgba(255, 152, 0, 0.2)'
                              }}>
                                <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>
                                  {capitalizeStateName(region)}:
                                </Typography>
                                {locations.map((loc, index) => (
                                  <Typography key={index} variant="body2" color="text.secondary" sx={{ 
                                    fontSize: '0.75rem', 
                                    ml: 0.5,
                                    lineHeight: 1.3
                                  }}>
                                    • {loc.location} ({loc.source === 'personal' ? 'Personal' : 
                                       loc.source === 'recent_job' ? 'Job' : 'University'})
                                  </Typography>
                                ))}
                              </Box>
                            ))}
                          </Box>
                          <Typography variant="body2" color="warning.dark" sx={{ 
                            fontStyle: 'italic', 
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            textAlign: 'center',
                            bgcolor: 'rgba(255, 152, 0, 0.1)',
                            p: 1,
                            borderRadius: 1
                          }}>
                            ⚠️ Please verify candidate's current location before proceeding with interviews
                          </Typography>
                        </Box>
                      );
                    } else if (inconsistencyCheck.locations.length > 1) {
                      // Show positive message when locations are consistent
                      const state = extractStateRegion(inconsistencyCheck.locations[0].location);
                      return (
                        <Box sx={{ 
                          mt: 2,
                          p: 2,
                          bgcolor: 'rgba(76, 175, 80, 0.08)',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'rgba(76, 175, 80, 0.25)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                              Location Consistency Verified
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                            All candidate locations are in the same region: <strong>{capitalizeStateName(state) || 'Same Area'}</strong>
                          </Typography>
                          <Box sx={{ ml: 2 }}>
                            {inconsistencyCheck.locations.map((loc, index) => (
                              <Typography key={index} variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                • {loc.location} ({loc.source === 'personal' ? 'Personal' : 
                                   loc.source === 'recent_job' ? 'Job' : 'University'})
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Professional Links */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 2, 
                    mt: 3,
                    p: 2,
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.05)'
                  }}>
                    {(validatedResult.candidateMetadata.profileLinks?.linkedin || validatedResult.candidateMetadata.linkedin) && (
                      <Button
                        variant="contained"
                        size="medium"
                        href={validatedResult.candidateMetadata.profileLinks?.linkedin || validatedResult.candidateMetadata.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<WebIcon />}
                        sx={{ 
                          textTransform: 'none',
                          color: 'white',
                          bgcolor: '#0077b5',
                          fontSize: '0.875rem',
                          py: 1,
                          px: 2.5,
                          minWidth: 'auto',
                          borderRadius: 2,
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(0, 119, 181, 0.3)',
                          '&:hover': {
                            bgcolor: '#005885',
                            boxShadow: '0 4px 12px rgba(0, 119, 181, 0.4)',
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        LinkedIn
                      </Button>
                    )}
                    {(validatedResult.candidateMetadata.profileLinks?.github || validatedResult.candidateMetadata.github) && (
                      <Button
                        variant="contained"
                        size="medium"
                        href={validatedResult.candidateMetadata.profileLinks?.github || validatedResult.candidateMetadata.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<WebIcon />}
                        sx={{ 
                          textTransform: 'none',
                          color: 'white',
                          bgcolor: '#333',
                          fontSize: '0.875rem',
                          py: 1,
                          px: 2.5,
                          minWidth: 'auto',
                          borderRadius: 2,
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(51, 51, 51, 0.3)',
                          '&:hover': {
                            bgcolor: '#000',
                            boxShadow: '0 4px 12px rgba(51, 51, 51, 0.4)',
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        GitHub
                      </Button>
                    )}
                    {(validatedResult.candidateMetadata.profileLinks?.portfolio || validatedResult.candidateMetadata.portfolio) && (
                      <Button
                        variant="contained"
                        size="medium"
                        href={validatedResult.candidateMetadata.profileLinks?.portfolio || validatedResult.candidateMetadata.portfolio}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<WebIcon />}
                        sx={{ 
                          textTransform: 'none',
                          color: 'white',
                          bgcolor: 'primary.main',
                          fontSize: '0.875rem',
                          py: 1,
                          px: 2.5,
                          minWidth: 'auto',
                          borderRadius: 2,
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Portfolio
                      </Button>
                )}
                    
                    {/* Missing Professional Links Guidance */}
                    {(!validatedResult.candidateMetadata.profileLinks?.linkedin && !validatedResult.candidateMetadata.linkedin) && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        mt: 1,
                        p: 1.5,
                        bgcolor: 'rgba(255, 152, 0, 0.05)',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'rgba(255, 152, 0, 0.2)'
                      }}>
                        <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                          LinkedIn not found
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                                LinkedIn Profile Not Available
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                                <strong>For HR:</strong> Check these sources for LinkedIn:
                              </Typography>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Search LinkedIn:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Search candidate's name on LinkedIn platform
                                </Typography>
                  </Box>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Resume Header:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Look for LinkedIn URL in resume contact section
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Ask Candidate:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Request LinkedIn profile URL directly
                                </Typography>
                              </Box>
                            </Box>
                          }
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'rgba(0, 0, 0, 0.9)',
                                maxWidth: 300,
                                fontSize: '0.8rem'
                              }
                            }
                          }}
                        >
                          <IconButton size="small" sx={{ color: 'warning.main', p: 0.5 }}>
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    
                    {(!validatedResult.candidateMetadata.profileLinks?.github && !validatedResult.candidateMetadata.github) && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        mt: 1,
                        p: 1.5,
                        bgcolor: 'rgba(255, 152, 0, 0.05)',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'rgba(255, 152, 0, 0.2)'
                      }}>
                        <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                          GitHub not found
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                                GitHub Profile Not Available
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                                <strong>For HR:</strong> Check these sources for GitHub:
                              </Typography>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Resume Contact:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Look for GitHub username in resume header
                                </Typography>
                              </Box>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Ask Candidate:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Request GitHub profile URL directly
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • LinkedIn Profile:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Check if GitHub is linked in LinkedIn profile
                                </Typography>
                              </Box>
                            </Box>
                          }
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'rgba(0, 0, 0, 0.9)',
                                maxWidth: 300,
                                fontSize: '0.8rem'
                              }
                            }
                          }}
                        >
                          <IconButton size="small" sx={{ color: 'warning.main', p: 0.5 }}>
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    
                    {(!validatedResult.candidateMetadata.profileLinks?.portfolio && !validatedResult.candidateMetadata.portfolio) && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        mt: 1,
                        p: 1.5,
                        bgcolor: 'rgba(255, 152, 0, 0.05)',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'rgba(255, 152, 0, 0.2)'
                      }}>
                        <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                          Portfolio not found
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                                Portfolio Website Not Available
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                                <strong>For HR:</strong> Check these sources for portfolio:
                              </Typography>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Resume Header:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Look for portfolio URL in resume contact section
                                </Typography>
                              </Box>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • Ask Candidate:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Request portfolio website URL directly
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                  • LinkedIn Profile:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 1 }}>
                                  Check if portfolio is linked in LinkedIn profile
                                </Typography>
                              </Box>
                            </Box>
                          }
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'rgba(0, 0, 0, 0.9)',
                                maxWidth: 300,
                                fontSize: '0.8rem'
                              }
                            }
                          }}
                        >
                          <IconButton size="small" sx={{ color: 'warning.main', p: 0.5 }}>
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    mt: 3,
                    p: 2,
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'rgba(25, 118, 210, 0.1)'
                  }}>
                <Chip
                      label={`${validatedResult.candidateMetadata.totalYearsExperience || 0} years experience`}
                      size="medium"
                      variant="filled"
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        height: 32
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              
              {/* Overall Score */}
              <Box sx={{ 
                textAlign: 'center',
                p: { xs: 2, sm: 3 }
              }}>
                <Box sx={{ position: 'relative', width: { xs: 100, sm: 120 }, height: { xs: 100, sm: 120 }, mx: 'auto' }}>
                  <CircularProgress
                    variant="determinate"
                    value={displayScores.overallComputed}
                    size={120}
                    thickness={4}
                    sx={{ 
                      width: '100% !important',
                      height: '100% !important',
                      color: displayScores.overallComputed >= 70 ? 'success.main' : 
                            displayScores.overallComputed >= 40 ? 'warning.main' : 'error.main',
                      filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))',
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round'
                      }
                    }}
                  />
                  <Box
                    sx={{ 
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25
                    }}
                  >
                    <Typography variant="h2" fontWeight={800} color="text.primary" sx={{ 
                      fontSize: { xs: '2rem', sm: '2.5rem' },
                      lineHeight: 1
                    }}>
                      {formatScore(displayScores.overallComputed)}
            </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ 
                      fontWeight: 600, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      lineHeight: 1
                    }}>
                      / 100
            </Typography>
                  </Box>
                </Box>
                <Chip
                  label={validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict || 'EVALUATING'}
                  color={getVerdictColor(validatedResult?.hrVerdict?.recommendation || validatedResult?.scores?.overall?.verdict)}
                  sx={{ 
                    mt: 1, 
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    height: { xs: 32, sm: 36 },
                    px: { xs: 2, sm: 2.5 },
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }
                  }}
                />
            </Box>
            </Box>

            {/* Quick Summary */}
            <Box sx={{ 
              mt: { xs: 2, sm: 3 }, 
              p: { xs: 2, sm: 2.5 }, 
              bgcolor: 'rgba(25, 118, 210, 0.03)', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'rgba(25, 118, 210, 0.1)',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.05)',
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto'
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ 
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                textTransform: 'uppercase',
                letterSpacing: 1
              }}>
                Role Alignment Summary
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ 
                fontWeight: 500,
                lineHeight: 1.6,
                fontSize: { xs: '0.85rem', sm: '0.9rem' }
              }}>
          {(() => {
                // Generate professional role alignment summary for client communication
                const candidateName = validatedResult?.candidateMetadata?.name || 'The candidate';
                const jobTitle = validatedResult?.inputData?.jobTitle || 'the position';
                const yearsExp = validatedResult?.candidateMetadata?.totalYearsExperience || 0;
                const matchedSkills = toArray(validatedResult?.keywordMatches?.mustHaveCovered || []);
                const missingSkills = toArray(validatedResult?.keywordMatches?.mustHaveMissing || []);
                const overallScore = validatedResult?.scores?.overall?.score || 0;
                const education = validatedResult?.candidateMetadata?.education || '';
                
                // Clean up job title for natural language
                const cleanJobTitle = jobTitle === 'the position' ? 'role' : `${jobTitle} role`;
                
                // Professional experience sentence
                let experienceSummary = '';
                if (yearsExp >= 5) {
                  experienceSummary = `${candidateName} brings ${yearsExp} years of solid professional experience. `;
                } else if (yearsExp >= 2) {
                  experienceSummary = `With ${yearsExp} years of professional experience, ${candidateName} demonstrates a developing skillset. `;
                } else if (yearsExp === 0 && education) {
                  experienceSummary = `${candidateName} is a recent graduate with limited professional work experience. `;
                } else {
                  experienceSummary = `${candidateName} currently has ${yearsExp} ${yearsExp === 1 ? 'year' : 'years'} of professional experience. `;
                }
                
                // Technical competency sentence
                let technicalSummary = '';
                if (matchedSkills.length > 0 && missingSkills.length > 0) {
                  const topSkills = matchedSkills.slice(0, 2).join(' and ');
                  const criticalMissing = missingSkills.slice(0, 2).join(' and ');
                  technicalSummary = `Shows strong capabilities in ${topSkills}, but has gaps in ${criticalMissing}. `;
                } else if (matchedSkills.length > 0) {
                  const topSkills = matchedSkills.slice(0, 2).join(' and ');
                  technicalSummary = `Demonstrates excellent alignment with role requirements, particularly in ${topSkills}. `;
                } else if (missingSkills.length > 0) {
                  const criticalMissing = missingSkills.slice(0, 2).join(' and ');
                  technicalSummary = `Has significant skill gaps in ${criticalMissing}. `;
                }
                
                // Overall recommendation
                let recommendation = '';
                if (overallScore >= 80) {
                  recommendation = `Strong alignment with high confidence for success.`;
                } else if (overallScore >= 60) {
                  recommendation = `Adequate alignment with room for growth.`;
                } else {
                  recommendation = `Significant gaps requiring considerable development investment.`;
                }
                
                return experienceSummary + technicalSummary + recommendation;
              })()}
                </Typography>
          </Box>
              </CardContent>
            </Card>
      )}


      {/* Tabbed Interface */}
      <Card sx={{ 
        borderRadius: { xs: 2, sm: 3, md: 4 }, 
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.06)',
        bgcolor: 'white',
        maxWidth: '1000px',
        mx: { xs: 0, sm: 'auto' }
      }}>
        <Box sx={{ 
          borderBottom: '2px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          bgcolor: '#f8f9fa'
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                minHeight: { xs: 52, sm: 56, md: 64 },
                minWidth: { xs: 80, sm: 100 },
                px: { xs: 1, sm: 2 },
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 700
                },
                '&:hover': {
                  color: 'primary.dark',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              },
              '& .MuiTabs-indicator': {
                height: { xs: 2, sm: 3 },
                borderRadius: '2px 2px 0 0'
              },
              '& .MuiTabs-scrollButtons': {
                width: { xs: 36, sm: 48 }
              }
            }}
          >
            <Tab label="Overview" />
            <Tab label="Experience" />
            <Tab label="Technical" />
            <Tab label="Education" />
            <Tab label="Communication" />
            <Tab label="Achievements" />
            <Tab label="Document Analysis" />
            <Tab label="HR Verdict" />
          </Tabs>
        </Box>

        <Box sx={{ minHeight: { xs: 300, sm: 400 } }}>
          {activeTab === 0 && <OverviewTab />}
          {activeTab === 1 && <ExperienceTab />}
          {activeTab === 2 && <TechnicalTab />}
          {activeTab === 3 && <EducationTab />}
          {activeTab === 4 && <CommunicationTab />}
          {activeTab === 5 && <AchievementsTab />}
          {activeTab === 6 && <DocumentAnalysisTab />}
          {activeTab === 7 && <HRVerdictTab />}
                </Box>
            </Card>



      {/* Document Preview Button */}
      {resumeContent && (
        <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, px: { xs: 2, sm: 0 } }}>
          <Tooltip title="Preview Resume Document">
            <Button
              onClick={() => setPreviewOpen(true)}
              variant="contained"
              startIcon={<VisibilityIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
                sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: { xs: 1.25, sm: 1.5 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 48,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Preview Resume
            </Button>
          </Tooltip>
            </Box>
      )}


      {/* Document Preview Dialog */}
      <DocumentPreview
        documentContent={resumeContent}
        skills={mustHaveSkills}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      {/* AI Disclaimer */}
      <Box sx={{ 
        mt: { xs: 2, sm: 2 }, 
        textAlign: 'center',
        px: { xs: 2, sm: 4, md: 6 },
        maxWidth: '1000px',
        mx: 'auto'
      }}>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            lineHeight: 1.4,
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            fontWeight: 400,
            opacity: 0.8,
            display: 'block'
          }}
        >
          ⚠️ <strong>NovaMatch assessments</strong> are based on resume and job description data. 
          Results support hiring decisions but should not be the sole basis for employment actions. 
          <em>Verify details independently. NovaMatch assumes no responsibility for outcomes.</em>
        </Typography>
      </Box>
    </Box>
  );

  // 4. ENHANCED FULLSCREEN MODE
  if (isFullscreen) {
    return (
      <Box className="fullscreen-overlay">
        <Box 
          className="exit-fullscreen"
          onClick={() => setIsFullscreen(false)}
          sx={{
            minWidth: 44,
            minHeight: 44
          }}
        >
          <CloseIcon />
        </Box>
        <Box 
          sx={{ 
            width: '100%', 
            maxWidth: { xs: '98vw', sm: '95vw' }, 
            maxHeight: { xs: '98vh', sm: '95vh' }, 
            overflow: 'auto',
            backgroundColor: 'white',
            borderRadius: { xs: 1, sm: 2 },
            p: { xs: 1, sm: 2 },
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}
        >
          {mainContent}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {mainContent}
      
      {/* Sidebar */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: sidebarOpen ? 0 : { xs: '-100%', sm: '-300px' },
          width: { xs: '100%', sm: '300px' },
          height: '100vh',
          backgroundColor: 'white',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          zIndex: 1300,
          transition: 'right 0.3s ease',
          overflowY: 'auto',
          borderLeft: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: { xs: 2, sm: 3 }
          }}>
            <Typography variant="h6" fontWeight={700} color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Quick Actions
            </Typography>
            <Button
              onClick={() => setSidebarOpen(false)}
              sx={{ minWidth: 'auto', p: 1, minHeight: 44, minWidth: 44 }}
            >
              <CloseIcon />
            </Button>
          </Box>
          
          <Stack spacing={2}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={downloadAssessment}
              startIcon={<DownloadIcon />}
              sx={{
                backgroundColor: 'success.main',
                '&:hover': {
                  backgroundColor: 'success.dark'
                },
                py: 0.5,
                minWidth: 'auto',
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              Download Assessment
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => onNewAssessment ? onNewAssessment() : navigate('/')}
              startIcon={<WebIcon />}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                },
                py: 0.5,
                minWidth: 'auto',
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              New Assessment
            </Button>
            
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 1.5 }}>
                Role Alignment Summary
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {(() => {
                  // Generate professional role alignment summary
                  const candidateName = validatedResult?.candidateMetadata?.name || 'The candidate';
                  const yearsExp = validatedResult?.candidateMetadata?.totalYearsExperience || 0;
                  const matchedSkills = toArray(validatedResult?.keywordMatches?.mustHaveCovered || []);
                  const missingSkills = toArray(validatedResult?.keywordMatches?.mustHaveMissing || []);
                  const overallScore = validatedResult?.scores?.overall?.score || 0;
                  
                  // Professional experience sentence
                  let experienceSummary = '';
                  if (yearsExp >= 5) {
                    experienceSummary = `${candidateName} brings ${yearsExp} years of solid professional experience. `;
                  } else if (yearsExp >= 2) {
                    experienceSummary = `With ${yearsExp} years of experience, ${candidateName} demonstrates a developing skillset. `;
                  } else {
                    experienceSummary = `${candidateName} has ${yearsExp} ${yearsExp === 1 ? 'year' : 'years'} of professional experience. `;
                  }
                  
                  // Technical competency sentence
                  let technicalSummary = '';
                  if (matchedSkills.length > 0 && missingSkills.length > 0) {
                    const topSkills = matchedSkills.slice(0, 2).join(' and ');
                    const criticalMissing = missingSkills.slice(0, 2).join(' and ');
                    technicalSummary = `Shows strong capabilities in ${topSkills}, but has gaps in ${criticalMissing}. `;
                  } else if (matchedSkills.length > 0) {
                    const topSkills = matchedSkills.slice(0, 2).join(' and ');
                    technicalSummary = `Demonstrates excellent alignment in ${topSkills}. `;
                  } else if (missingSkills.length > 0) {
                    const criticalMissing = missingSkills.slice(0, 2).join(' and ');
                    technicalSummary = `Has skill gaps in ${criticalMissing}. `;
                  }
                  
                  // Overall recommendation
                  let recommendation = '';
                  if (overallScore >= 80) {
                    recommendation = `Strong alignment with high confidence for success.`;
                  } else if (overallScore >= 60) {
                    recommendation = `Adequate alignment with room for growth.`;
                  } else {
                    recommendation = `Significant gaps requiring development.`;
                  }
                  
                  return experienceSummary + technicalSummary + recommendation;
                })()}
              </Typography>
            </Box>
            
            <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Quick Stats
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Experience: {displayScores.expScore}/28
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Technical: {displayScores.skillsScore}/26
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Education: {displayScores.eduScore}/20
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Communication: {displayScores.commScore}/16
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
      
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1299,
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </Box>
  );
};

export default NovaMatchResult;

